import { Fragment, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, ScrollText, Plus, Bell, Send, Sparkles, Coins, TrendingUp,
  AlertTriangle, RefreshCw, Pencil, Trash2, Check, FileText, Search,
  Building2, DollarSign, Wallet, FileDown, Calendar, Download, X, Printer, HandCoins,
  Filter, SlidersHorizontal, ChevronDown, ChevronUp, RotateCcw, Camera, Image as ImageIcon, ChevronLeft, ChevronRight
} from 'lucide-react';
import LogModal from '../components/LogModal';
import PriceInput from '../components/PriceInput';
import ZoomablePhoto from '../components/ZoomablePhoto';
import logService from '../services/logService';
import { API_URL, API_HOST } from '../config/api';
import useEscapeClose from '../hooks/useEscapeClose';
import { handlePrint } from '../utils/printHelper';
import WholesalerMobileHome from './WholesalerMobileHome';
import { buildWholesalerReportModel, exportWholesalerReportExcel, exportWholesalerReportPdf } from '../utils/wholesalerFullReport';
import './WholesalerPayments.css';

// mobileApp: iOS uygulamasının WebView'ı (/mobile/toptancilar, MobileToptancilar.js) — ana görünüm
// telefona göre WholesalerMobileHome ile çizilir; modallar ve tüm hesaplar ortaktır.

// API DATE alanını (UTC ISO string) yerel YYYY-MM-DD'ye çevirir; toISOString TR saatinde 1 gün geri kaydırıyordu
const toLocalYMD = (v) => {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Arama için Türkçe uyumlu küçük harf: "NADİR" → "nadir" (toLowerCase "i̇" üretip eşleşmeyi bozuyordu);
// ı/i ayrımı da yok sayılır ("nadır" yazınca da bulsun)
const searchFold = (v) => String(v || '').toLocaleLowerCase('tr-TR').normalize('NFD').replace(/\u0307/g, '').replace(/ı/g, 'i');

const WholesalerPayments = ({ user, selectedStore, mobileApp = false }) => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [wholesalers, setWholesalers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [originalPayment, setOriginalPayment] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveSearch, setArchiveSearch] = useState('');
  const [archiveFilterType, setArchiveFilterType] = useState('all');
  const [archiveFilterCurrency, setArchiveFilterCurrency] = useState('all');
  const [archiveFilterCategory, setArchiveFilterCategory] = useState('all');
  const [archiveFilterMethod, setArchiveFilterMethod] = useState('all');
  const [archiveFilterDateFrom, setArchiveFilterDateFrom] = useState('');
  const [archiveFilterDateTo, setArchiveFilterDateTo] = useState('');
  const [archiveSortBy, setArchiveSortBy] = useState('date_desc');
  const [archiveShowFilters, setArchiveShowFilters] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');

  // Arama ve filtreler
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedWholesalers, setExpandedWholesalers] = useState({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterDebtStatus, setFilterDebtStatus] = useState('all'); // all, hasDebt, noDebt
  const [filterCurrencyType, setFilterCurrencyType] = useState('all');
  const [filterSortBy, setFilterSortBy] = useState('debt_desc'); // debt_desc, debt_asc, name_asc, name_desc

  // Özet zaman filtresi
  const [summaryPeriod, setSummaryPeriod] = useState('current'); // all, current, thisMonth, lastMonth, thisYear, custom
  const [summaryCustomFrom, setSummaryCustomFrom] = useState('');
  const [summaryCustomTo, setSummaryCustomTo] = useState('');

  // Yeni Toptancı modal state
  const [showWholesalerModal, setShowWholesalerModal] = useState(false);
  const [editingWholesalerData, setEditingWholesalerData] = useState(null);
  const [wholesalerFormData, setWholesalerFormData] = useState({
    name: '', company_name: '', phone: '', email: '', address: '', notes: ''
  });

  // Toptancı Detay Modal
  const [showWDetail, setShowWDetail] = useState(false);
  const [selectedWId, setSelectedWId] = useState(null);
  const [wHistories, setWHistories] = useState([]);
  const [expandedPayGroups, setExpandedPayGroups] = useState({});
  const [wNewDebt, setWNewDebt] = useState({ title: '', amount: '', currency: 'USD', due_date: '', payment_type: 'expense', photos: [] });
  const [wPayAmount, setWPayAmount] = useState('');
  const [wPayMethod, setWPayMethod] = useState('cash');
  const [wPayNote, setWPayNote] = useState('');
  const [wPayCurrency, setWPayCurrency] = useState('');
  const [wEditingProfile, setWEditingProfile] = useState(false);
  const [showStandaloneDebt, setShowStandaloneDebt] = useState(false);
  const [standaloneDebt, setStandaloneDebt] = useState({ title: '', amount: '', currency: 'USD', due_date: '', photos: [] });
  // Borç/alacak kaydı fotoğrafları: yükleme durumu + büyük görüntüleyici ({ photos, index })
  const [wsPhotoUploading, setWsPhotoUploading] = useState(false);
  const [wsPhotoViewer, setWsPhotoViewer] = useState(null);
  // Tam rapor (tüm toptancılar + kayıtlar + ödeme geçmişi) — Excel / PDF
  const [showFullReportMenu, setShowFullReportMenu] = useState(false);
  const [fullReportLoading, setFullReportLoading] = useState(null); // 'excel' | 'pdf' | null
  const [wDetailTab, setWDetailTab] = useState('history');
  const [wProfileForm, setWProfileForm] = useState({ name: '', company_name: '', phone: '', email: '', address: '', notes: '' });

  // Özet detay modal
  const [showSummaryDetailModal, setShowSummaryDetailModal] = useState(false);
  const [summaryDetailType, setSummaryDetailType] = useState('debt');

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    currency: 'USD',
    payment_type: 'expense',
    category: 'wholesaler',
    recipient: '',
    due_date: '',
    reminder_days: 3,
    recurring: false,
    recurring_interval: 'monthly',
    status: 'pending',
    notes: '',
    invoice_issued: false,
    is_wholesaler: true,
    wholesaler_id: '',
    photos: []
  });

  // Kısmi ödeme state
  const [showPartialModal, setShowPartialModal] = useState(false);
  const [partialPayment, setPartialPayment] = useState(null);
  const [partialAmount, setPartialAmount] = useState('');
  const [partialNewAmount, setPartialNewAmount] = useState('');
  const [partialPaymentMethod, setPartialPaymentMethod] = useState('cash');
  const [partialNote, setPartialNote] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [editingHistoryAmount, setEditingHistoryAmount] = useState('');
  const [editingHistoryMethod, setEditingHistoryMethod] = useState('');

  // Ödendi onay modal state
  const [showPaidModal, setShowPaidModal] = useState(false);
  const [paidPayment, setPaidPayment] = useState(null);
  const [paidPaymentMethod, setPaidPaymentMethod] = useState('cash');

  // Ödeme detay modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailPayment, setDetailPayment] = useState(null);

  // Rapor modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportDateRange, setReportDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [reportFormat, setReportFormat] = useState('pdf');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);

  // Modal kapanış animasyonu için
  const [closingModal, setClosingModal] = useState(null);

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [!!wsPhotoViewer, () => setWsPhotoViewer(null)],
    [showSummaryDetailModal, () => setShowSummaryDetailModal(false)],
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showLogModal, () => {}], // LogModal handles its own ESC
    [showDetailModal, () => setShowDetailModal(false)],
    [showReportModal, () => setShowReportModal(false)],
    [showPaidModal, () => setShowPaidModal(false)],
    [showArchiveModal, () => setShowArchiveModal(false)],
    [showPartialModal, () => setShowPartialModal(false)],
    [showNotifications, () => setShowNotifications(false)],
    [showWholesalerModal, () => setShowWholesalerModal(false)],
    [showWDetail, () => setShowWDetail(false)],
    [showModal, () => setShowModal(false)],
  ]);

  // Modal açıkken arka sayfa scroll'unu kilitle
  useEffect(() => {
    const anyOpen = showModal || showWDetail || showPartialModal || showPaidModal
      || showDetailModal || showWholesalerModal || showStandaloneDebt
      || showConfirmModal || showReportModal || showSummaryDetailModal
      || showArchiveModal;
    const prev = document.body.style.overflow;
    if (anyOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = prev; };
  }, [showModal, showWDetail, showPartialModal, showPaidModal, showDetailModal,
      showWholesalerModal, showStandaloneDebt, showConfirmModal, showReportModal,
      showSummaryDetailModal, showArchiveModal]);

  // Modal kapanış handler'ı
  const handleCloseModal = (modalName, setShowModal) => {
    setClosingModal(modalName);
    setTimeout(() => {
      setShowModal(false);
      setClosingModal(null);
    }, 250);
  };

  // Para birimi bilgileri
  const currencyInfo = {
    TRY: { name: 'Türk Lirası', symbol: '₺', icon: '🇹🇷' },
    USD: { name: 'Amerikan Doları', symbol: '$', icon: '🇺🇸' },
    EUR: { name: 'Euro', symbol: '€', icon: '🇪🇺' },
    GOLD: { name: 'Has Altın', symbol: 'gr Has Altın', icon: '🥇' },
    SILVER: { name: 'Has Gümüş', symbol: 'gr Has Gümüş', icon: '🥈' }
  };

  // Kategori bilgileri
  const categoryNames = {
    wholesaler: 'Toptancı',
    supplier: 'Tedarikçi',
    importer: 'İthalatçı',
    manufacturer: 'Üretici',
    distributor: 'Distribütör',
    broker: 'Komisyoncu',
    foreign_trade: 'Dış Ticaret',
    other: 'Diğer'
  };

  const categoryIcons = {
    wholesaler: '🏢',
    supplier: '🚚',
    importer: '🚢',
    manufacturer: '🏭',
    distributor: '📦',
    broker: '🤝',
    foreign_trade: '🌍',
    other: '📌'
  };

  const statusNames = {
    pending: 'Bekliyor',
    paid: 'Ödendi',
    partial: 'Kısmi Ödeme',
    cancelled: 'İptal',
    overdue: 'Gecikmiş'
  };

  const recurringIntervalNames = {
    weekly: 'Haftalık',
    monthly: 'Aylık',
    yearly: 'Yıllık'
  };

  // Ödeme yöntemi bilgileri
  const paymentMethodInfo = {
    cash: { name: 'Nakit', icon: '🧾' },
    card: { name: 'Kart', icon: '💳' },
    bank_transfer: { name: 'Havale/EFT', icon: '🏦' },
    check: { name: 'Çek', icon: '📝' },
    gold: { name: 'Altın', icon: '🥇' },
    silver: { name: 'Gümüş', icon: '🥈' },
    other: { name: 'Diğer', icon: '📋' }
  };

  // Döviz kurları. Toptancı borcu TL karşılıkları ANLIK SATIŞ kuruyla hesaplanır (özel
  // kur/makas/manuel kur UYGULANMAZ): borcu kapatmak için döviz/has piyasadan alınır, yani
  // ödenen fiyat satış kurudur. Eski manuel özel kurlar (ör. EUR 50, gümüş 147) toplamı
  // ciddi saptırıyordu (2026-10-09).
  const [prices, setPrices] = useState(null);
  // Kayan kur bandındaki ▲/▼ yönü için bir önceki çekimin kurları
  const [prevPrices, setPrevPrices] = useState(null);

  const fetchRates = async () => {
    try {
      const res = await fetch(`${API_URL}/prices`);
      const data = await res.json();
      setPrices(old => {
        if (old) setPrevPrices(old);
        return data;
      });
    } catch (e) { /* kurlar opsiyonel */ }
  };

  const tickerItems = useMemo(() => {
    if (!prices) return [];
    return [
      { label: 'USD', key: 'USD', digits: 4 },
      { label: 'EUR', key: 'EUR', digits: 4 },
      { label: 'GBP', key: 'GBP', digits: 4 },
      { label: 'Has Altın', key: 'altin', digits: 2 },
      { label: '22 Ayar', key: 'altin_22', digits: 2 },
      { label: '14 Ayar', key: 'altin_14', digits: 2 },
      { label: 'Gümüş', key: 'gumus', digits: 2 },
      { label: 'Çeyrek', key: 'ceyrek_yeni', digits: 2, alisKey: 'ceyrek_yeni_alis', satisKey: 'ceyrek_yeni' },
    ].map(it => {
      const satisKey = it.satisKey || `${it.key}_satis`;
      const alisKey = it.alisKey || `${it.key}_alis`;
      const satis = parseFloat(prices[satisKey]) || parseFloat(prices[it.key]) || 0;
      const alis = parseFloat(prices[alisKey]) || 0;
      const prev = parseFloat(prevPrices?.[satisKey]) || parseFloat(prevPrices?.[it.key]) || 0;
      const dir = !prev || Math.abs(satis - prev) < 1e-9 ? 0 : (satis > prev ? 1 : -1);
      return { ...it, satis, alis, dir };
    }).filter(it => it.satis > 0);
  }, [prices, prevPrices]);

  const liveRates = useMemo(() => {
    const satisRate = (currency) => {
      if (currency === 'GOLD') {
        return parseFloat(prices?.altin_satis) || parseFloat(prices?.altin) || 0;
      }
      if (currency === 'SILVER') {
        return parseFloat(prices?.gumus_satis) || parseFloat(prices?.gumus) || 0;
      }
      return parseFloat(prices?.[`${currency}_satis`]) || parseFloat(prices?.[currency]) || 0;
    };

    return {
      TRY: 1,
      USD: satisRate('USD'),
      EUR: satisRate('EUR'),
      GOLD: satisRate('GOLD'),
      SILVER: satisRate('SILVER')
    };
  }, [prices]);

  const toTRY = (amount, currency) => {
    const rate = liveRates[currency];
    if (!rate || currency === 'TRY' || !amount) return null;
    return Math.round(amount * rate);
  };

  useEffect(() => {
    fetchPayments();
    fetchWholesalers();
    fetchRates();
    const rateInterval = setInterval(fetchRates, 60000);
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
    return () => clearInterval(rateInterval);
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/wholesaler-payments`);
      const data = await response.json();
      setPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Toptancı ödemeleri yüklenemedi:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchWholesalers = async () => {
    try {
      const response = await fetch(`${API_URL}/wholesalers?active=true`);
      const data = await response.json();
      setWholesalers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Toptancılar yüklenemedi:', error);
    }
  };

  // Format helpers
  const formatCurrency = (amount, currency) => {
    const info = currencyInfo[currency] || currencyInfo.TRY;
    const formatted = Number(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (currency === 'GOLD' || currency === 'SILVER') {
      return `${formatted} ${info.symbol}`;
    }
    return `${info.symbol}${formatted}`;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Gecikmiş ödemeler
  const getOverduePayments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return payments.filter(p => {
      if (p.status !== 'pending' || !p.due_date) return false;
      const dueDate = new Date(p.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    });
  };

  // Hatırlatma ödemeleri
  const getReminderPayments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return payments
      .filter(p => {
        if (p.status !== 'pending' || !p.due_date) return false;
        const dueDate = new Date(p.due_date);
        dueDate.setHours(0, 0, 0, 0);
        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= (p.reminder_days || 3) && diffDays >= -30;
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };

  // (eski filtre/tab/özet fonksiyonları kaldırıldı - tek sayfa toptancı görünümü)

  // Bildirim işlemleri
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        toast.success('Bildirim izni verildi!');
        new Notification('Toptancı Ödeme Hatırlatıcı', {
          body: 'Bildirimler aktif edildi.',
          icon: '💰'
        });
      }
    }
  };

  const sendBrowserNotification = (payment) => {
    if (notificationPermission === 'granted') {
      const dueDate = new Date(payment.due_date);
      const today = new Date();
      const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

      let message = '';
      if (diffDays < 0) {
        message = `${Math.abs(diffDays)} gün gecikti! Tutar: ${formatCurrency(payment.amount, payment.currency)}`;
      } else if (diffDays === 0) {
        message = `Bugün ödenmesi gerekiyor! Tutar: ${formatCurrency(payment.amount, payment.currency)}`;
      } else {
        message = `${diffDays} gün sonra. Tutar: ${formatCurrency(payment.amount, payment.currency)}`;
      }

      new Notification(payment.title, {
        body: message,
        icon: '💰',
        tag: `wholesaler-payment-${payment.id}`
      });
    }
  };

  const sendAllReminders = () => {
    const reminders = getReminderPayments();
    reminders.forEach((payment, index) => {
      setTimeout(() => sendBrowserNotification(payment), index * 1000);
    });
    toast.success(`${reminders.length} hatırlatma bildirimi gönderildi`);
  };

  // Toptancı değişikliği
  const handleWholesalerChange = (wholesalerId) => {
    const id = wholesalerId ? parseInt(wholesalerId) : '';
    if (id) {
      const wholesaler = wholesalers.find(w => w.id === id);
      setFormData(prev => ({
        ...prev,
        wholesaler_id: id,
        recipient: wholesaler ? wholesaler.name : prev.recipient
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        wholesaler_id: '',
        recipient: ''
      }));
    }
  };

  // CRUD işlemleri
  // Tam rapor: veriyi her seferinde sunucudan taze çeker (arşivliler ve tüm ödeme geçmişi dahil)
  const generateFullReport = async (format) => {
    setShowFullReportMenu(false);
    setFullReportLoading(format);
    try {
      const res = await fetch(`${API_URL}/wholesaler-payments/full-report`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      const model = buildWholesalerReportModel(data, { currencyInfo, statusNames, paymentMethodInfo });
      if (format === 'pdf') await exportWholesalerReportPdf(model);
      else exportWholesalerReportExcel(model);
      toast.success(format === 'pdf' ? 'PDF raporu indirildi!' : 'Excel raporu indirildi!');
      logService.log('wholesaler-payments', 'export', `Toptancı tam raporu indirildi (${format === 'pdf' ? 'PDF' : 'Excel'})`, {
        details: { toptanci: data.wholesalers?.length || 0, kayit: data.payments?.length || 0, odeme: data.history?.length || 0 },
        status: 'success'
      });
    } catch (error) {
      console.error('Tam rapor hatası:', error);
      toast.error('Rapor oluşturulamadı!');
    } finally {
      setFullReportLoading(null);
    }
  };

  // ===== Borç/Alacak kaydı fotoğrafları =====
  const parseWsPhotos = (photos) => {
    if (!photos) return [];
    if (Array.isArray(photos)) return photos;
    try { const arr = JSON.parse(photos); return Array.isArray(arr) ? arr : []; } catch (e) { return []; }
  };

  const uploadWsPhotos = async (fileList, onUploaded) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setWsPhotoUploading(true);
    const fd = new FormData();
    files.forEach(f => fd.append('photos', f));
    try {
      const res = await fetch(`${API_URL}/wholesaler-payments/upload-photos`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success && Array.isArray(data.photos)) onUploaded(data.photos);
      else toast.error(data.error || 'Fotoğraf yüklenemedi!');
    } catch (error) {
      toast.error('Fotoğraf yüklenemedi!');
    } finally {
      setWsPhotoUploading(false);
    }
  };

  // Fotoğraf seçici: Kamera + Galeri butonları ve küçük resimler (tıklayınca büyük görüntüleyici)
  const renderWsPhotoPicker = (photos, setPhotos, idPrefix) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      <input type="file" id={`${idPrefix}-camera`} accept="image/*" capture="environment" style={{ display: 'none' }}
        onChange={e => { uploadWsPhotos(e.target.files, added => setPhotos([...(photos || []), ...added])); e.target.value = ''; }} />
      <input type="file" id={`${idPrefix}-gallery`} accept="image/*" multiple style={{ display: 'none' }}
        onChange={e => { uploadWsPhotos(e.target.files, added => setPhotos([...(photos || []), ...added])); e.target.value = ''; }} />
      <button type="button" disabled={wsPhotoUploading} onClick={() => document.getElementById(`${idPrefix}-camera`)?.click()}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', color: '#475569', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
        <Camera size={15} /> Kamera
      </button>
      <button type="button" disabled={wsPhotoUploading} onClick={() => document.getElementById(`${idPrefix}-gallery`)?.click()}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', color: '#475569', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
        <ImageIcon size={15} /> Galeri
      </button>
      {wsPhotoUploading && <span style={{ fontSize: '12px', color: '#64748b' }}>Yükleniyor...</span>}
      {(photos || []).map((photo, i) => (
        <div key={photo + i} style={{ position: 'relative', width: '44px', height: '44px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0 }}>
          <img src={`${API_HOST}${photo}`} alt={`Fotoğraf ${i + 1}`} title="Büyüt"
            onClick={() => setWsPhotoViewer({ photos, index: i })}
            style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} />
          <button type="button" title="Fotoğrafı kaldır" onClick={() => setPhotos(photos.filter((_, pi) => pi !== i))}
            style={{ position: 'absolute', top: 0, right: 0, width: '15px', height: '15px', border: 'none', borderRadius: '0 5px 0 5px', background: '#ef4444', color: '#fff', fontSize: '10px', lineHeight: 1, padding: 0, cursor: 'pointer' }}>✕</button>
        </div>
      ))}
    </div>
  );

  const handleSave = async () => {
    if (!formData.title || !formData.amount) {
      toast.warning('Başlık ve tutar zorunludur!');
      return;
    }

    try {
      const url = editingPayment
        ? `${API_URL}/wholesaler-payments/${editingPayment.id}`
        : `${API_URL}/wholesaler-payments`;
      const method = editingPayment ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        is_wholesaler: true,
        created_by: user?.id
      };
      // Düzenlerken paid_amount'ı koru
      if (editingPayment) {
        const paid = Number(editingPayment.paid_amount) || 0;
        payload.paid_amount = paid;
        // Ödeme yapılmış kayıtta tür/birim değişirse ödenen tutar anlamsızlaşır → engelle
        if (paid > 0 && (formData.payment_type || 'expense') !== (editingPayment.payment_type || 'expense')) {
          toast.warning('Ödeme yapılmış kaydın türü (Borç/Alacak) değiştirilemez. Önce ödemeleri silin.');
          return;
        }
        if (paid > 0 && (formData.currency || 'TRY') !== (editingPayment.currency || 'TRY')) {
          toast.warning('Ödeme yapılmış kaydın para birimi değiştirilemez. Önce ödemeleri silin.');
          return;
        }
        if (paid > 0 && (Number(formData.amount) || 0) < paid) {
          toast.warning(`Tutar, ödenmiş miktardan (${formatCurrency(paid, editingPayment.currency || 'TRY')}) az olamaz.`);
          return;
        }
        // Tutar değiştiyse ve durum elle değiştirilmediyse durumu ödenen tutara göre yeniden belirle
        const newAmount = Number(formData.amount) || 0;
        const amountChanged = newAmount !== (Number(editingPayment.amount) || 0);
        const statusUntouched = formData.status === (editingPayment.status || 'pending');
        if (amountChanged && statusUntouched && formData.status !== 'cancelled') {
          payload.status = paid <= 0 ? 'pending' : (paid >= newAmount ? 'paid' : 'partial');
        }
      }
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        if (editingPayment) {
          const changes = [];
          if (originalPayment?.title !== formData.title) {
            changes.push(`Başlık: "${originalPayment?.title}" → "${formData.title}"`);
          }
          if (String(originalPayment?.amount) !== String(formData.amount)) {
            changes.push(`Tutar: ₺${Number(originalPayment?.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} → ₺${Number(formData.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
          }

          logService.log('wholesaler-payments', 'update',
            `Toptancı ödemesi güncellendi: ${formData.title}`, {
            entity_type: 'wholesaler-payment',
            entity_id: editingPayment.id,
            details: { baslik: formData.title, degisiklikler: changes },
            status: 'success'
          });
        } else {
          logService.log('wholesaler-payments', 'create',
            `Yeni toptancı ödemesi: ${formData.title}`, {
            entity_type: 'wholesaler-payment',
            entity_id: data.payment?.id,
            details: { baslik: formData.title, tutar: formatCurrency(formData.amount, formData.currency) },
            status: 'success'
          });
        }

        toast.success(editingPayment ? 'Ödeme güncellendi!' : 'Ödeme oluşturuldu!');
        setShowModal(false);
        resetForm();
        fetchPayments();
      } else {
        toast.error(data.error || 'İşlem başarısız!');
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      toast.error('İşlem başarısız!');
    }
  };

  const updatePaymentStatus = async (payment, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/wholesaler-payments/${payment.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        logService.log('wholesaler-payments', 'update',
          `Ödeme durumu değişti: ${payment.title} → ${statusNames[newStatus]}`, {
          entity_type: 'wholesaler-payment',
          entity_id: payment.id,
          status: 'success'
        });

        toast.success(`Ödeme ${statusNames[newStatus].toLowerCase()} olarak işaretlendi!`);
        fetchPayments();
      }
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      toast.error('Güncelleme başarısız!');
    }
  };

  const toggleInvoiceStatus = async (payment) => {
    try {
      const newStatus = !payment.invoice_issued;
      const response = await fetch(`${API_URL}/wholesaler-payments/${payment.id}/invoice`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_issued: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(newStatus ? 'Fatura kesildi olarak işaretlendi' : 'Fatura işareti kaldırıldı');
        fetchPayments();
      }
    } catch (error) {
      console.error('Fatura durumu güncelleme hatası:', error);
      toast.error('Fatura durumu güncellenemedi!');
    }
  };

  const openPartialModal = async (payment) => {
    setPartialPayment(payment);
    setPartialAmount(payment.paid_amount || 0);
    setPartialNewAmount('');
    setPartialPaymentMethod(payment.payment_method || 'cash');
    setPartialNote('');
    setShowPartialModal(true);
    // Ödeme geçmişini çek
    try {
      const res = await fetch(`${API_URL}/wholesaler-payments/${payment.id}/history`);
      const data = await res.json();
      setPaymentHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      setPaymentHistory([]);
    }
  };

  const openPaidModal = (payment) => {
    setPaidPayment(payment);
    setPaidPaymentMethod(payment.payment_method || 'cash');
    setShowPaidModal(true);
  };

  const handlePartialPayment = async () => {
    if (!partialPayment || !partialNewAmount) {
      toast.warning('Lütfen yeni ödeme tutarı girin!');
      return;
    }

    const prevPaid = Number(partialPayment.paid_amount) || 0;
    const newPay = parseFloat(partialNewAmount);
    const totalAmount = parseFloat(partialPayment.amount);
    const totalPaid = prevPaid + newPay;

    if (newPay <= 0) {
      toast.warning('Geçersiz tutar!');
      return;
    }

    const submit = async () => {
      try {
        const newStatus = totalPaid >= totalAmount ? 'paid' : 'partial';
        const response = await fetch(`${API_URL}/wholesaler-payments/${partialPayment.id}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: newStatus,
            paid_amount: totalPaid,
            partial_amount: newPay,
            payment_method: partialPaymentMethod,
            note: partialNote.trim() || undefined
          })
        });

        const data = await response.json();
        if (data.success || response.ok) {
          const remaining = Math.max(0, totalAmount - totalPaid);
          if (data.overpayment) {
            toast.success(`Borç kapatıldı! Fazla ${formatCurrency(data.overpayment.amount, data.overpayment.currency || partialPayment.currency)} alacak olarak eklendi.`);
          } else {
            toast.success(newStatus === 'paid'
              ? 'Ödeme tamamlandı!'
              : `Kısmi ödeme kaydedildi! Kalan: ${formatCurrency(remaining, partialPayment.currency)}`
            );
          }
          setShowPartialModal(false);
          setPartialPayment(null);
          setPartialAmount('');
          setPartialNewAmount('');
          setPartialPaymentMethod('cash');
          setPartialNote('');
          fetchPayments();
        }
      } catch (error) {
        console.error('Kısmi ödeme hatası:', error);
        toast.error('İşlem başarısız!');
      }
    };

    if (totalPaid > totalAmount) {
      const excess = totalPaid - totalAmount;
      showConfirmation({
        title: 'Fazla Ödeme Algılandı',
        message: `Borç ${formatCurrency(totalAmount, partialPayment.currency)}, toplam ödeme ${formatCurrency(totalPaid, partialPayment.currency)}.\n\nAşan ${formatCurrency(excess, partialPayment.currency)} aynı toptancıya alacak olarak eklenecek. Devam edilsin mi?`,
        confirmText: 'Evet, Alacak Oluştur',
        cancelText: 'Vazgeç',
        type: 'warning',
        onConfirm: submit
      });
      return;
    }

    submit();
  };

  const handleUpdateHistory = async (historyId, newAmount, newMethod) => {
    if (!partialPayment) return;
    const amount = parseFloat(newAmount);
    if (isNaN(amount) || amount < 0) {
      toast.warning('Geçersiz tutar!');
      return;
    }
    try {
      const body = { amount, payment_id: partialPayment.id };
      if (newMethod) body.payment_method = newMethod;
      const res = await fetch(`${API_URL}/wholesaler-payment-history/${historyId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        // History listesini güncelle
        setPaymentHistory(prev => prev.map(h => h.id === historyId ? { ...h, amount: String(amount), ...(newMethod ? { payment_method: newMethod } : {}) } : h));
        // Ana ödemeyi güncelle (yeni paid_amount backend'den geliyor)
        setPartialPayment(prev => ({ ...prev, paid_amount: data.new_paid_amount }));
        setEditingHistoryId(null);
        setEditingHistoryAmount('');
        setEditingHistoryMethod('');
        toast.success('Ödeme güncellendi!');
        fetchPayments();
      }
    } catch (error) {
      console.error('Geçmiş güncelleme hatası:', error);
      toast.error('Güncelleme başarısız!');
    }
  };

  const handleDeleteHistory = async (historyId) => {
    if (!partialPayment) return;
    try {
      const res = await fetch(`${API_URL}/wholesaler-payment-history/${historyId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_id: partialPayment.id })
      });
      const data = await res.json();
      if (data.success) {
        setPaymentHistory(prev => prev.filter(h => h.id !== historyId));
        setPartialPayment(prev => ({ ...prev, paid_amount: data.new_paid_amount }));
        toast.success('Ödeme kaydı silindi!');
        fetchPayments();
      }
    } catch (error) {
      console.error('Geçmiş silme hatası:', error);
      toast.error('Silme başarısız!');
    }
  };

  const handleArchive = async (paymentId, archived = true) => {
    try {
      const res = await fetch(`${API_URL}/wholesaler-payments/${paymentId}/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(archived ? 'Ödeme arşivlendi!' : 'Arşivden çıkarıldı!');
        fetchPayments();
      }
    } catch (error) {
      toast.error('İşlem başarısız!');
    }
  };

  const handlePaidConfirm = async () => {
    if (!paidPayment) return;

    try {
      const response = await fetch(`${API_URL}/wholesaler-payments/${paidPayment.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'paid',
          paid_amount: paidPayment.amount,
          partial_amount: Number(paidPayment.amount) - (Number(paidPayment.paid_amount) || 0),
          payment_method: paidPaymentMethod
        })
      });

      const data = await response.json();
      if (data.success) {
        logService.log('wholesaler-payments', 'update',
          `Ödeme durumu değişti: ${paidPayment.title} → Ödendi (${paymentMethodInfo[paidPaymentMethod]?.name})`, {
          entity_type: 'wholesaler-payment',
          entity_id: paidPayment.id,
          status: 'success'
        });

        toast.success(`Ödeme "${paymentMethodInfo[paidPaymentMethod]?.name}" olarak kaydedildi!`);
        setShowPaidModal(false);
        setPaidPayment(null);
        setPaidPaymentMethod('cash');
        fetchPayments();
      }
    } catch (error) {
      console.error('Ödeme onay hatası:', error);
      toast.error('İşlem başarısız!');
    }
  };

  // ========== RAPOR FONKSİYONLARI ==========

  // Rapor için filtrelenmiş ödemeleri getir
  const getReportPayments = () => {
    const start = new Date(reportDateRange.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(reportDateRange.endDate);
    end.setHours(23, 59, 59, 999);

    return payments.filter(p => {
      if (!p.due_date) return false;
      const dueDate = new Date(p.due_date);
      return dueDate >= start && dueDate <= end;
    }).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };

  // Rapor özeti hesapla
  const getReportSummary = () => {
    const reportPayments = getReportPayments();
    const summary = {
      totalPayments: reportPayments.length,
      totalExpense: 0,
      totalIncome: 0,
      paidCount: 0,
      pendingCount: 0,
      overdueCount: 0,
      byCurrency: {},
      byMethod: {},
      byCategory: {},
      byStatus: {}
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    reportPayments.forEach(p => {
      // Toplam hesapla
      if (p.payment_type === 'expense') {
        summary.totalExpense += Number(p.amount || 0);
      } else {
        summary.totalIncome += Number(p.amount || 0);
      }

      // Durum sayıları
      if (p.status === 'paid') summary.paidCount++;
      if (p.status === 'pending') summary.pendingCount++;
      if (p.status === 'pending' && p.due_date && new Date(p.due_date) < today) summary.overdueCount++;

      // Para birimi bazlı
      const curr = p.currency || 'TRY';
      if (!summary.byCurrency[curr]) {
        summary.byCurrency[curr] = { expense: 0, income: 0, paid: 0 };
      }
      if (p.payment_type === 'expense') {
        summary.byCurrency[curr].expense += Number(p.amount || 0);
      } else {
        summary.byCurrency[curr].income += Number(p.amount || 0);
      }
      if (p.status === 'paid') {
        summary.byCurrency[curr].paid += Number(p.paid_amount || p.amount || 0);
      }

      // Ödeme yöntemi bazlı
      if (p.status === 'paid' && p.payment_method) {
        if (!summary.byMethod[p.payment_method]) {
          summary.byMethod[p.payment_method] = { count: 0, total: 0 };
        }
        summary.byMethod[p.payment_method].count++;
        summary.byMethod[p.payment_method].total += Number(p.paid_amount || p.amount || 0);
      }

      // Kategori bazlı
      const cat = p.category || 'other';
      if (!summary.byCategory[cat]) {
        summary.byCategory[cat] = { count: 0, expense: 0, income: 0 };
      }
      summary.byCategory[cat].count++;
      if (p.payment_type === 'expense') {
        summary.byCategory[cat].expense += Number(p.amount || 0);
      } else {
        summary.byCategory[cat].income += Number(p.amount || 0);
      }

      // Durum bazlı
      const status = p.status || 'pending';
      if (!summary.byStatus[status]) {
        summary.byStatus[status] = { count: 0, amount: 0 };
      }
      summary.byStatus[status].count++;
      summary.byStatus[status].amount += Number(p.amount || 0);
    });

    return summary;
  };

  // Tarih aralığı kısayolları
  const setDateRangePreset = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'thisWeek':
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        end = today;
        break;
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last3Months':
        start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        end = today;
        break;
      case 'thisYear':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      default:
        return;
    }

    setReportDateRange({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });
  };

  // PDF oluştur
  const generatePDF = async () => {
    setReportLoading(true);
    try {
      const reportPayments = getReportPayments();
      const summary = getReportSummary();

      // PDF içeriğini HTML olarak oluştur ve yazdır
      const startDateFormatted = new Date(reportDateRange.startDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
      const endDateFormatted = new Date(reportDateRange.endDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Ödeme Raporu - ${startDateFormatted} / ${endDateFormatted}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
              font-size: 11px;
              line-height: 1.4;
              color: #1e293b;
              padding: 20px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 3px solid #0f766e;
            }
            .header h1 {
              font-size: 24px;
              color: #0f766e;
              margin-bottom: 8px;
            }
            .header .date-range {
              font-size: 14px;
              color: #64748b;
            }
            .header .company {
              font-size: 16px;
              font-weight: 600;
              color: #334155;
              margin-top: 4px;
            }
            .generated-date {
              font-size: 10px;
              color: #94a3b8;
              margin-top: 8px;
            }

            .summary-section {
              margin-bottom: 30px;
              padding: 20px;
              background: #f8fafc;
              border-radius: 12px;
              border: 1px solid #e2e8f0;
            }
            .summary-section h2 {
              font-size: 16px;
              color: #334155;
              margin-bottom: 16px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e2e8f0;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 16px;
            }
            .summary-card {
              background: white;
              padding: 16px;
              border-radius: 10px;
              border: 1px solid #e2e8f0;
            }
            .summary-card .label {
              font-size: 11px;
              color: #64748b;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .summary-card .value {
              font-size: 20px;
              font-weight: 700;
              margin-top: 4px;
            }
            .summary-card .value.expense { color: #ef4444; }
            .summary-card .value.income { color: #22c55e; }
            .summary-card .value.total { color: #0f766e; }

            .currency-summary {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
              gap: 12px;
              margin-top: 20px;
            }
            .currency-card {
              background: white;
              padding: 14px;
              border-radius: 8px;
              border: 1px solid #e2e8f0;
            }
            .currency-card .header {
              display: flex;
              align-items: center;
              gap: 8px;
              font-weight: 600;
              margin-bottom: 10px;
              padding-bottom: 8px;
              border-bottom: 1px solid #f1f5f9;
            }
            .currency-card .row {
              display: flex;
              justify-content: space-between;
              padding: 4px 0;
              font-size: 12px;
            }
            .currency-card .row .label { color: #64748b; }
            .currency-card .row .value { font-weight: 600; }
            .currency-card .row .value.expense { color: #ef4444; }
            .currency-card .row .value.income { color: #22c55e; }

            .payments-section {
              margin-top: 30px;
            }
            .payments-section h2 {
              font-size: 16px;
              color: #334155;
              margin-bottom: 16px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e2e8f0;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
            }
            th {
              background: #0f766e;
              color: white;
              padding: 10px 8px;
              text-align: left;
              font-weight: 600;
              font-size: 10px;
            }
            td {
              padding: 10px 8px;
              border-bottom: 1px solid #e2e8f0;
              vertical-align: top;
            }
            tr:nth-child(even) { background: #f8fafc; }
            tr:hover { background: #f1f5f9; }

            .status-badge {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 12px;
              font-size: 9px;
              font-weight: 600;
              text-transform: uppercase;
            }
            .status-pending { background: #fef3c7; color: #d97706; }
            .status-paid { background: #dcfce7; color: #16a34a; }
            .status-partial { background: #e0f2f1; color: #0f766e; }
            .status-cancelled { background: #fee2e2; color: #dc2626; }
            .status-overdue { background: #fee2e2; color: #dc2626; }

            .invoice-badge {
              display: inline-block;
              padding: 2px 6px;
              border-radius: 4px;
              font-size: 9px;
              font-weight: 600;
            }
            .invoice-issued { background: #dcfce7; color: #16a34a; }
            .invoice-not-issued { background: #fee2e2; color: #dc2626; }

            .amount { font-weight: 700; }
            .amount.expense { color: #ef4444; }
            .amount.income { color: #22c55e; }

            .method-badge {
              display: inline-block;
              padding: 2px 6px;
              border-radius: 4px;
              font-size: 9px;
              background: #f1f5f9;
              color: #475569;
            }

            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #e2e8f0;
              text-align: center;
              color: #94a3b8;
              font-size: 10px;
            }

            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📊 Toptancı Ödeme Raporu</h1>
            <div class="company">Akik Kuyumculuk</div>
            <div class="date-range">${startDateFormatted} - ${endDateFormatted}</div>
            <div class="generated-date">Rapor Oluşturulma: ${new Date().toLocaleString('tr-TR')}</div>
          </div>

          <div class="summary-section">
            <h2>📈 Özet Bilgiler</h2>
            <div class="summary-grid">
              <div class="summary-card">
                <div class="label">Toplam İşlem</div>
                <div class="value total">${summary.totalPayments}</div>
              </div>
              <div class="summary-card">
                <div class="label">Toplam Borç</div>
                <div class="value expense">₺${summary.totalExpense.toLocaleString()}</div>
              </div>
              <div class="summary-card">
                <div class="label">Toplam Alacak</div>
                <div class="value income">₺${summary.totalIncome.toLocaleString()}</div>
              </div>
              <div class="summary-card">
                <div class="label">Net Bakiye</div>
                <div class="value ${summary.totalIncome - summary.totalExpense >= 0 ? 'income' : 'expense'}">
                  ${summary.totalIncome - summary.totalExpense >= 0 ? '+' : ''}₺${(summary.totalIncome - summary.totalExpense).toLocaleString('tr-TR')}
                </div>
              </div>
            </div>

            <div class="summary-grid" style="margin-top: 16px;">
              <div class="summary-card">
                <div class="label">Ödenen</div>
                <div class="value income">${summary.paidCount} adet</div>
              </div>
              <div class="summary-card">
                <div class="label">Bekleyen</div>
                <div class="value" style="color: #d97706;">${summary.pendingCount} adet</div>
              </div>
              <div class="summary-card">
                <div class="label">Gecikmiş</div>
                <div class="value expense">${summary.overdueCount} adet</div>
              </div>
              <div class="summary-card">
                <div class="label">Ödeme Oranı</div>
                <div class="value total">${summary.totalPayments > 0 ? Math.round((summary.paidCount / summary.totalPayments) * 100) : 0}%</div>
              </div>
            </div>

            ${Object.keys(summary.byCurrency).length > 0 ? `
              <h3 style="margin-top: 20px; margin-bottom: 12px; font-size: 14px; color: #475569;">💰 Para Birimi Bazlı Dağılım</h3>
              <div class="currency-summary">
                ${Object.entries(summary.byCurrency).map(([curr, data]) => `
                  <div class="currency-card">
                    <div class="header">${currencyInfo[curr]?.icon || '💰'} ${currencyInfo[curr]?.name || curr}</div>
                    <div class="row">
                      <span class="label">Borç:</span>
                      <span class="value expense">${formatCurrency(data.expense, curr)}</span>
                    </div>
                    <div class="row">
                      <span class="label">Alacak:</span>
                      <span class="value income">${formatCurrency(data.income, curr)}</span>
                    </div>
                    <div class="row">
                      <span class="label">Ödenen:</span>
                      <span class="value" style="color: #0f766e;">${formatCurrency(data.paid, curr)}</span>
                    </div>
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </div>

          <div class="payments-section">
            <h2>📋 Ödeme Detayları (${reportPayments.length} kayıt)</h2>
            <table>
              <thead>
                <tr>
                  <th style="width: 90px;">Vade Tarihi</th>
                  <th>Alıcı / Açıklama</th>
                  <th style="width: 80px;">Kategori</th>
                  <th style="width: 70px;">Tür</th>
                  <th style="width: 100px;">Tutar</th>
                  <th style="width: 70px;">Durum</th>
                  <th style="width: 70px;">Fatura</th>
                  <th style="width: 80px;">Ödeme Tarihi</th>
                  <th style="width: 70px;">Yöntem</th>
                </tr>
              </thead>
              <tbody>
                ${reportPayments.map(p => {
                  const dueDate = new Date(p.due_date);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isOverdue = p.status === 'pending' && dueDate < today;

                  return `
                    <tr>
                      <td>${dueDate.toLocaleDateString('tr-TR')}</td>
                      <td>
                        <strong>${p.recipient || '-'}</strong><br>
                        <span style="color: #64748b;">${p.title}</span>
                        ${p.notes ? `<br><small style="color: #94a3b8;">${p.notes}</small>` : ''}
                      </td>
                      <td>${categoryIcons[p.category] || '📌'} ${categoryNames[p.category] || p.category}</td>
                      <td>${p.payment_type === 'expense' ? '📤 Borç' : '📥 Alacak'}</td>
                      <td class="amount ${p.payment_type}">${p.payment_type === 'expense' ? '-' : '+'}${formatCurrency(p.amount, p.currency)}</td>
                      <td><span class="status-badge status-${isOverdue ? 'overdue' : p.status}">${isOverdue ? 'Gecikmiş' : statusNames[p.status]}</span></td>
                      <td><span class="invoice-badge ${p.invoice_issued ? 'invoice-issued' : 'invoice-not-issued'}">${p.invoice_issued ? '✓ Kesildi' : '✗ Kesilmedi'}</span></td>
                      <td>${p.paid_date ? new Date(p.paid_date).toLocaleDateString('tr-TR') : '-'}</td>
                      <td>${p.payment_method ? `<span class="method-badge">${paymentMethodInfo[p.payment_method]?.icon} ${paymentMethodInfo[p.payment_method]?.name}</span>` : '-'}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>

          <div class="footer">
            <p>Bu rapor Akik ERP sistemi tarafından otomatik olarak oluşturulmuştur.</p>
            <p style="margin-top: 4px;">© ${new Date().getFullYear()} Akik Kuyumculuk - Tüm hakları saklıdır.</p>
          </div>

          <script>
            window.onload = function() {
              var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 500); }
            }
          </script>
        </body>
        </html>
      `;

      handlePrint(html, 'receipt');

      toast.success('Rapor oluşturuldu! Yazdırma penceresi açıldı.');
    } catch (error) {
      console.error('PDF oluşturma hatası:', error);
      toast.error('Rapor oluşturulamadı!');
    } finally {
      setReportLoading(false);
    }
  };

  // Excel/CSV export
  const generateExcel = () => {
    setReportLoading(true);
    try {
      const reportPayments = getReportPayments();

      // CSV header
      const headers = [
        'Vade Tarihi',
        'Ödeme Tarihi',
        'Alıcı',
        'Açıklama',
        'Kategori',
        'Tür',
        'Tutar',
        'Para Birimi',
        'Durum',
        'Ödeme Yöntemi',
        'Fatura Durumu',
        'Notlar'
      ];

      // CSV rows
      const rows = reportPayments.map(p => [
        new Date(p.due_date).toLocaleDateString('tr-TR'),
        p.paid_date ? new Date(p.paid_date).toLocaleDateString('tr-TR') : '',
        p.recipient || '',
        p.title || '',
        categoryNames[p.category] || p.category,
        p.payment_type === 'expense' ? 'Borç' : 'Alacak',
        p.amount || 0,
        p.currency || 'TRY',
        statusNames[p.status] || p.status,
        p.payment_method ? paymentMethodInfo[p.payment_method]?.name : '',
        p.invoice_issued ? 'Kesildi' : 'Kesilmedi',
        p.notes || ''
      ]);

      // BOM for UTF-8
      const BOM = '\uFEFF';
      const csvContent = BOM + [
        headers.join(';'),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      ].join('\n');

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const startDateFormatted = new Date(reportDateRange.startDate).toLocaleDateString('tr-TR').replace(/\./g, '-');
      const endDateFormatted = new Date(reportDateRange.endDate).toLocaleDateString('tr-TR').replace(/\./g, '-');

      link.setAttribute('href', url);
      link.setAttribute('download', `odeme-raporu_${startDateFormatted}_${endDateFormatted}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Excel dosyası indirildi!');
    } catch (error) {
      console.error('Excel oluşturma hatası:', error);
      toast.error('Excel dosyası oluşturulamadı!');
    } finally {
      setReportLoading(false);
    }
  };

  // Rapor önizleme güncelle
  const updateReportPreview = () => {
    setReportPreview(getReportSummary());
  };

  // Tarih değiştiğinde önizlemeyi güncelle
  useEffect(() => {
    if (showReportModal) {
      updateReportPreview();
    }
  }, [reportDateRange, showReportModal, payments]);

  const handleDelete = async (payment) => {
    showConfirmation({
      title: 'Ödeme Sil',
      message: `"${payment.title}" ödemesini silmek istediğinize emin misiniz?`,
      confirmText: 'Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/wholesaler-payments/${payment.id}`, {
            method: 'DELETE'
          });

          const data = await response.json();
          if (data.success) {
            logService.log('wholesaler-payments', 'delete',
              `Ödeme silindi: ${payment.title}`, {
              entity_type: 'wholesaler-payment',
              entity_id: payment.id,
              status: 'success'
            });
            toast.success('Ödeme silindi!');
            fetchPayments();
          }
        } catch (error) {
          console.error('Silme hatası:', error);
          toast.error('Silme başarısız!');
        }
      }
    });
  };

  const openEditModal = (payment) => {
    setEditingPayment(payment);
    const formValues = {
      title: payment.title || '',
      description: payment.description || '',
      amount: payment.amount || '',
      currency: payment.currency || 'TRY',
      payment_type: payment.payment_type || 'expense',
      category: payment.category || 'wholesaler',
      recipient: payment.recipient || '',
      due_date: toLocalYMD(payment.due_date),
      reminder_days: payment.reminder_days || 3,
      recurring: payment.recurring === 1 || payment.recurring === true,
      recurring_interval: payment.recurring_interval || 'monthly',
      status: payment.status || 'pending',
      notes: payment.notes || '',
      invoice_issued: payment.invoice_issued === 1 || payment.invoice_issued === true,
      is_wholesaler: true,
      wholesaler_id: payment.wholesaler_id || '',
      photos: parseWsPhotos(payment.photos)
    };
    setOriginalPayment({ ...formValues });
    setFormData(formValues);
    setShowModal(true);
  };

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingPayment(null);
    setOriginalPayment(null);
    setFormData({
      title: '',
      description: '',
      amount: '',
      currency: 'USD',
      payment_type: 'expense',
      category: 'wholesaler',
      recipient: '',
      due_date: '',
      reminder_days: 3,
      recurring: false,
      recurring_interval: 'monthly',
      status: 'pending',
      notes: '',
      invoice_issued: false,
      is_wholesaler: true,
      wholesaler_id: ''
    });
  };

  // (eski resetFilters ve mainActiveFilterCount kaldırıldı)

  const getNextOccurrence = (dueDate, interval) => {
    const date = new Date(dueDate);
    switch (interval) {
      case 'weekly': date.setDate(date.getDate() + 7); break;
      case 'monthly': date.setMonth(date.getMonth() + 1); break;
      case 'yearly': date.setFullYear(date.getFullYear() + 1); break;
      default: break;
    }
    return date;
  };

  // Gün farkı hesapla
  const getDaysDiff = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  // Tüm toptancılar + ödemeleri (ana veri)
  const wholesalerGroups = useMemo(() => {
    const search = searchFold(searchTerm);
    const groups = [];

    wholesalers.forEach(w => {
      const wPayments = payments.filter(p => !p.archived && p.wholesaler_id === w.id);

      // Arama filtresi
      if (searchTerm) {
        const matchName = searchFold(w.name).includes(search);
        const matchCompany = searchFold(w.company_name).includes(search);
        const matchPayment = wPayments.some(p => searchFold(p.title).includes(search));
        if (!matchName && !matchCompany && !matchPayment) return;
      }

      const totalByCurrency = {};
      wPayments.forEach(p => {
        const curr = p.currency || 'TRY';
        if (!totalByCurrency[curr]) totalByCurrency[curr] = { total: 0, paid: 0, remaining: 0, credit: 0, net: 0 };
        const total = Number(p.amount) || 0;
        const isPaid = p.status === 'paid';
        const paidAmt = isPaid ? total : (Number(p.paid_amount) || 0);
        const openAmt = isPaid ? 0 : Math.max(0, total - paidAmt);
        if (p.payment_type === 'income') {
          totalByCurrency[curr].credit += openAmt;
        } else {
          totalByCurrency[curr].total += total;
          totalByCurrency[curr].paid += paidAmt;
          totalByCurrency[curr].remaining += openAmt;
        }
      });
      Object.keys(totalByCurrency).forEach(c => {
        totalByCurrency[c].net = totalByCurrency[c].remaining - totalByCurrency[c].credit;
      });

      groups.push({
        key: `w_${w.id}`,
        wholesaler: w,
        wholesaler_id: w.id,
        name: w.name,
        company_name: w.company_name,
        payments: wPayments,
        totalByCurrency,
        pendingCount: wPayments.filter(p => p.status === 'pending' || p.status === 'partial').length,
        paidCount: wPayments.filter(p => p.status === 'paid').length
      });
    });

    // Toptancısız ödemeler
    const orphaned = payments.filter(p => !p.archived && !p.wholesaler_id);
    if (orphaned.length > 0) {
      const matchOrphan = !searchTerm || orphaned.some(p =>
        searchFold(p.title).includes(search) || searchFold(p.recipient).includes(search)
      );
      if (matchOrphan) {
        const totalByCurrency = {};
        orphaned.forEach(p => {
          const curr = p.currency || 'TRY';
          if (!totalByCurrency[curr]) totalByCurrency[curr] = { total: 0, paid: 0, remaining: 0, credit: 0, net: 0 };
          const total = Number(p.amount) || 0;
          const isPaid = p.status === 'paid';
          const paidAmt = isPaid ? total : (Number(p.paid_amount) || 0);
          const openAmt = isPaid ? 0 : Math.max(0, total - paidAmt);
          if (p.payment_type === 'income') {
            totalByCurrency[curr].credit += openAmt;
          } else {
            totalByCurrency[curr].total += total;
            totalByCurrency[curr].paid += paidAmt;
            totalByCurrency[curr].remaining += openAmt;
          }
        });
        Object.keys(totalByCurrency).forEach(c => {
          totalByCurrency[c].net = totalByCurrency[c].remaining - totalByCurrency[c].credit;
        });
        groups.push({
          key: 'other',
          wholesaler: null,
          wholesaler_id: null,
          name: 'Diğer',
          company_name: '',
          payments: orphaned,
          totalByCurrency,
          pendingCount: orphaned.filter(p => p.status === 'pending' || p.status === 'partial').length,
          paidCount: orphaned.filter(p => p.status === 'paid').length
        });
      }
    }

    // Filtreler
    let filtered = groups;

    // Borç durumu filtresi
    if (filterDebtStatus === 'hasDebt') {
      filtered = filtered.filter(g => Object.values(g.totalByCurrency).some(v => v.remaining > 0));
    } else if (filterDebtStatus === 'noDebt') {
      filtered = filtered.filter(g => Object.values(g.totalByCurrency).every(v => v.remaining <= 0));
    }

    // Para birimi filtresi
    if (filterCurrencyType !== 'all') {
      filtered = filtered.filter(g => g.totalByCurrency[filterCurrencyType] && g.totalByCurrency[filterCurrencyType].total > 0);
    }

    // Sıralama
    return filtered.sort((a, b) => {
      const aR = Object.values(a.totalByCurrency).reduce((s, v) => s + v.remaining, 0);
      const bR = Object.values(b.totalByCurrency).reduce((s, v) => s + v.remaining, 0);
      switch (filterSortBy) {
        case 'debt_asc': return aR - bR;
        case 'name_asc': return (a.name || '').localeCompare(b.name || '', 'tr');
        case 'name_desc': return (b.name || '').localeCompare(a.name || '', 'tr');
        default: // debt_desc
          if (aR > 0 && bR === 0) return -1;
          if (aR === 0 && bR > 0) return 1;
          return bR - aR;
      }
    });
  }, [wholesalers, payments, searchTerm, filterDebtStatus, filterCurrencyType, filterSortBy]);

  // Özet zaman aralığı (created_at filtresi)
  const summaryDateRange = useMemo(() => {
    const now = new Date();
    const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
    const endOfDay = (d) => { const x = new Date(d); x.setHours(23,59,59,999); return x; };
    if (summaryPeriod === 'thisMonth') {
      return { from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
    }
    if (summaryPeriod === 'lastMonth') {
      return { from: startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)) };
    }
    if (summaryPeriod === 'thisYear') {
      return { from: startOfDay(new Date(now.getFullYear(), 0, 1)), to: endOfDay(new Date(now.getFullYear(), 11, 31)) };
    }
    if (summaryPeriod === 'custom') {
      const from = summaryCustomFrom ? startOfDay(new Date(summaryCustomFrom)) : null;
      const to = summaryCustomTo ? endOfDay(new Date(summaryCustomTo)) : null;
      return { from, to };
    }
    return { from: null, to: null }; // all / current
  }, [summaryPeriod, summaryCustomFrom, summaryCustomTo]);

  // Genel özet istatistikleri
  const summaryStats = useMemo(() => {
    const { from, to } = summaryDateRange;
    const activePayments = payments.filter(p => {
      if (p.archived) return false;
      if (summaryPeriod === 'current') {
        // Sadece açık (ödenmemiş) kayıtlar
        if (p.status === 'paid' || p.status === 'cancelled') return false;
      }
      if (from || to) {
        const d = p.created_at ? new Date(p.created_at) : (p.due_date ? new Date(p.due_date) : null);
        if (!d) return false;
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      return true;
    });
    const byCurrency = {};
    let overdueCount = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    activePayments.forEach(p => {
      const curr = p.currency || 'TRY';
      if (!byCurrency[curr]) byCurrency[curr] = { total: 0, paid: 0, remaining: 0, overdue: 0, credit: 0 };
      const total = Number(p.amount) || 0;
      const isPaid = p.status === 'paid';
      const paidAmt = isPaid ? total : (Number(p.paid_amount) || 0);
      const openAmt = isPaid ? 0 : Math.max(0, total - paidAmt);
      if (p.payment_type === 'income') {
        byCurrency[curr].credit += openAmt;
      } else {
        byCurrency[curr].total += total;
        byCurrency[curr].paid += paidAmt;
        byCurrency[curr].remaining += openAmt;
        if (!isPaid && p.due_date && new Date(p.due_date) < today) {
          byCurrency[curr].overdue += openAmt;
          overdueCount++;
        }
      }
    });

    // TL karşılıkları (USD/EUR döviz satış, altın/gümüş has satış kuruyla — liveRates)
    let totalTotalTRY = 0, totalDebtTRY = 0, totalPaidTRY = 0, totalOverdueTRY = 0, totalCreditTRY = 0;
    Object.entries(byCurrency).forEach(([curr, data]) => {
      if (curr === 'TRY') {
        totalTotalTRY += data.total;
        totalDebtTRY += data.remaining;
        totalPaidTRY += data.paid;
        totalOverdueTRY += data.overdue;
        totalCreditTRY += data.credit;
      } else {
        const rate = liveRates[curr];
        if (rate) {
          totalTotalTRY += data.total * rate;
          totalDebtTRY += data.remaining * rate;
          totalPaidTRY += data.paid * rate;
          totalOverdueTRY += data.overdue * rate;
          totalCreditTRY += data.credit * rate;
        }
      }
    });

    return {
      byCurrency,
      totalTotalTRY,
      totalDebtTRY,
      totalPaidTRY,
      totalOverdueTRY,
      totalCreditTRY,
      overdueCount,
      wholesalerCount: wholesalers.length,
      activePaymentCount: activePayments.length
    };
  }, [payments, wholesalers, liveRates, summaryPeriod, summaryDateRange]);

  const openSummaryDetail = (type) => {
    setSummaryDetailType(type);
    setShowSummaryDetailModal(true);
  };

  // Toptancıya borç ekle
  const openNewPaymentForWholesaler = (wholesaler) => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      wholesaler_id: wholesaler.id,
      recipient: wholesaler.name
    }));
    setShowModal(true);
  };

  // Yeni toptancı kaydet
  const handleSaveWholesaler = async () => {
    if (!wholesalerFormData.name) {
      toast.warning('Toptancı adı zorunludur!');
      return;
    }
    try {
      const url = editingWholesalerData
        ? `${API_URL}/wholesalers/${editingWholesalerData.id}`
        : `${API_URL}/wholesalers`;
      const method = editingWholesalerData ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...wholesalerFormData, active: true })
      });
      const data = await response.json();
      if (data.success || data.id) {
        toast.success(editingWholesalerData ? 'Toptancı güncellendi!' : 'Toptancı eklendi!');
        setShowWholesalerModal(false);
        setEditingWholesalerData(null);
        setWholesalerFormData({ name: '', company_name: '', phone: '', email: '', address: '', notes: '' });
        fetchWholesalers();
      } else {
        toast.error(data.error || 'İşlem başarısız!');
      }
    } catch (error) {
      console.error('Toptancı kayıt hatası:', error);
      toast.error('İşlem başarısız!');
    }
  };

  // Toptancı düzenle
  const openEditWholesaler = (wholesaler) => {
    setEditingWholesalerData(wholesaler);
    setWholesalerFormData({
      name: wholesaler.name || '',
      company_name: wholesaler.company_name || '',
      phone: wholesaler.phone || '',
      email: wholesaler.email || '',
      address: wholesaler.address || '',
      notes: wholesaler.notes || ''
    });
    setShowWholesalerModal(true);
  };

  // Toptancı sil
  const handleDeleteWholesaler = (wholesaler) => {
    showConfirmation({
      title: 'Toptancı Sil',
      message: `"${wholesaler.name}" toptancısını ve tüm borç kayıtlarını silmek istediğinize emin misiniz?`,
      confirmText: 'Sil',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/wholesalers/${wholesaler.id}`, { method: 'DELETE' });
          const data = await response.json();
          if (data.success) {
            toast.success('Toptancı silindi!');
            fetchWholesalers();
            fetchPayments();
          } else {
            toast.error(data.error || 'Silme başarısız!');
          }
        } catch (error) {
          toast.error('Silme başarısız!');
        }
      }
    });
  };

  // ===== Toptancı Detay Modal Fonksiyonları =====
  const openWholesalerDetail = (wholesalerId, tab, currency) => {
    setSelectedWId(wholesalerId);
    setShowWDetail(true);
    setWDetailTab(tab || 'history');
    setWNewDebt({ title: '', amount: '', currency: currency || 'USD', due_date: '', payment_type: 'expense', photos: [] });
    setWPayAmount('');
    setWPayMethod('cash');
    setWPayNote('');
    setWPayCurrency(currency || '');
    setWEditingProfile(false);
    const ws = wholesalers.find(x => x.id === wholesalerId);
    if (ws) setWProfileForm({ name: ws.name || '', company_name: ws.company_name || '', phone: ws.phone || '', email: ws.email || '', address: ws.address || '', notes: ws.notes || '' });
  };

  // Profil kaydet (detay modal içinden)
  const handleWDetailSaveProfile = async () => {
    if (!wProfileForm.name || !selectedWId) {
      toast.warning('Toptancı adı zorunludur!');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/wholesalers/${selectedWId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...wProfileForm, active: true })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Toptancı güncellendi!');
        setWEditingProfile(false);
        fetchWholesalers();
      } else {
        toast.error(data.error || 'Güncelleme başarısız!');
      }
    } catch (error) {
      toast.error('Güncelleme başarısız!');
    }
  };

  // Toptancı sil (detay modal içinden)
  const handleWDetailDelete = () => {
    const group = wholesalerGroups.find(g => g.wholesaler_id === selectedWId);
    if (!group) return;
    showConfirmation({
      title: 'Toptancı Sil',
      message: `"${group.name}" toptancısını silmek istediğinize emin misiniz? Bekleyen borçları varsa silinemez.`,
      confirmText: 'Sil',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await fetch(`${API_URL}/wholesalers/${selectedWId}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            toast.success('Toptancı silindi!');
            setShowWDetail(false);
            fetchWholesalers();
            fetchPayments();
          } else {
            toast.error(data.error || 'Silme başarısız!');
          }
        } catch (error) {
          toast.error('Silme başarısız!');
        }
      }
    });
  };

  // Geçmiş ödemeleri çek
  useEffect(() => {
    if (showWDetail) {
      const group = selectedWId
        ? wholesalerGroups.find(g => g.wholesaler_id === selectedWId)
        : wholesalerGroups.find(g => g.key === 'other');
      if (group && group.payments.length > 0) {
        Promise.all(
          group.payments.map(p =>
            fetch(`${API_URL}/wholesaler-payments/${p.id}/history`)
              .then(r => r.json())
              .then(data => (Array.isArray(data) ? data : []).map(h => ({ ...h, currency: p.currency || 'TRY' })))
          )
        ).then(results => setWHistories(results.flat())).catch(() => setWHistories([]));
      } else {
        setWHistories([]);
      }
    }
  }, [showWDetail, selectedWId, payments]);

  // Detay modalından borç / alacak ekle
  const handleWDetailAddDebt = async () => {
    if (!wNewDebt.title || !wNewDebt.amount || !selectedWId) {
      toast.warning('Açıklama ve tutar zorunludur!');
      return;
    }
    const isCredit = wDetailTab === 'addCredit';
    const group = wholesalerGroups.find(g => g.wholesaler_id === selectedWId);
    try {
      const res = await fetch(`${API_URL}/wholesaler-payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: wNewDebt.title,
          amount: wNewDebt.amount,
          currency: wNewDebt.currency,
          due_date: wNewDebt.due_date || null,
          photos: wNewDebt.photos || [],
          payment_type: isCredit ? 'income' : 'expense',
          category: 'wholesaler',
          status: 'pending',
          recipient: group?.name || '',
          wholesaler_id: selectedWId,
          is_wholesaler: true,
          created_by: user?.id
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(isCredit ? 'Alacak eklendi!' : 'Borç eklendi!');
        setShowWDetail(false);
        fetchPayments();
      }
    } catch (error) {
      toast.error(isCredit ? 'Alacak eklenemedi!' : 'Borç eklenemedi!');
    }
  };

  const handleStandaloneAddDebt = async () => {
    if (!standaloneDebt.title || !standaloneDebt.amount) {
      toast.warning('Açıklama ve tutar zorunludur!');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/wholesaler-payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: standaloneDebt.title,
          amount: standaloneDebt.amount,
          currency: standaloneDebt.currency,
          due_date: standaloneDebt.due_date || null,
          photos: standaloneDebt.photos || [],
          payment_type: 'expense',
          category: 'wholesaler',
          status: 'pending',
          recipient: '',
          wholesaler_id: null,
          is_wholesaler: true,
          created_by: user?.id
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Borç eklendi!');
        setShowStandaloneDebt(false);
        setStandaloneDebt({ title: '', amount: '', currency: 'USD', due_date: '', photos: [] });
        fetchPayments();
      }
    } catch (error) {
      toast.error('Borç eklenemedi!');
    }
  };

  // Detay modalından ödeme yap (birim bazlı)
  // Ödeme seçili birimdeki açık borçlara EN ESKİDEN başlayarak dağıtılır; tüm borçları aşan kısım
  // son borca yazılır ve backend bunu aynı toptancıya alacak olarak açar.
  const handleWDetailPayment = async (currencyOverride) => {
    const currency = currencyOverride || wPayCurrency;
    if (!wPayAmount || !currency) return;
    const group = selectedWId
      ? wholesalerGroups.find(g => g.wholesaler_id === selectedWId)
      : wholesalerGroups.find(g => g.key === 'other');
    if (!group) return;
    const openDebts = group.payments
      .filter(p => (p.status === 'pending' || p.status === 'partial') && (p.currency || 'TRY') === currency && p.payment_type !== 'income')
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id - b.id);
    if (openDebts.length === 0) {
      toast.warning('Bu birimde ödenmemiş borç yok!');
      return;
    }
    const newPay = parseFloat(wPayAmount);
    if (!(newPay > 0)) return;
    const round2 = v => Math.round(v * 100) / 100;

    // Dağıtım planı
    const plan = [];
    let left = round2(newPay);
    openDebts.forEach((debt, i) => {
      if (left <= 0) return;
      const prevPaid = Number(debt.paid_amount) || 0;
      const debtAmount = Number(debt.amount) || 0;
      const open = round2(Math.max(0, debtAmount - prevPaid));
      const isLast = i === openDebts.length - 1;
      const apply = isLast ? left : Math.min(left, open);
      if (apply <= 0) return;
      plan.push({ debt, apply, totalPaid: round2(prevPaid + apply), debtAmount });
      left = round2(left - apply);
    });
    const totalOpen = round2(openDebts.reduce((s, d) => s + Math.max(0, (Number(d.amount) || 0) - (Number(d.paid_amount) || 0)), 0));
    const note = wPayNote.trim();

    const submit = async () => {
      let overpayment = null;
      let done = 0;
      try {
        for (const step of plan) {
          const res = await fetch(`${API_URL}/wholesaler-payments/${step.debt.id}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              status: step.totalPaid >= step.debtAmount ? 'paid' : 'partial',
              paid_amount: step.totalPaid,
              partial_amount: step.apply,
              payment_method: wPayMethod,
              note: note || undefined
            })
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json().catch(() => ({}));
          if (data.overpayment) overpayment = data.overpayment;
          done++;
        }
        if (overpayment) {
          toast.success(`Borç kapatıldı! Fazla ${formatCurrency(overpayment.amount, overpayment.currency || currency)} alacak olarak eklendi.`);
        } else {
          const remaining = round2(totalOpen - newPay);
          toast.success(remaining <= 0 ? 'Borç tamamen ödendi!' : `Ödeme kaydedildi! Kalan: ${formatCurrency(remaining, currency)}`);
        }
        setWPayAmount('');
        setWPayNote('');
        setShowWDetail(false);
      } catch (error) {
        toast.error(done > 0 ? `Ödemenin bir kısmı kaydedilemedi (${done}/${plan.length} borç işlendi). Lütfen geçmişi kontrol edin.` : 'Ödeme başarısız!');
      } finally {
        fetchPayments();
      }
    };

    if (newPay > totalOpen) {
      const excess = round2(newPay - totalOpen);
      showConfirmation({
        title: 'Fazla Ödeme Algılandı',
        message: `Kalan borç ${formatCurrency(totalOpen, currency)}, ödeme ${formatCurrency(newPay, currency)}.\n\nAşan ${formatCurrency(excess, currency)} aynı toptancıya alacak olarak eklenecek. Devam edilsin mi?`,
        confirmText: 'Evet, Alacak Oluştur',
        cancelText: 'Vazgeç',
        type: 'warning',
        onConfirm: submit
      });
      return;
    }

    submit();
  };

  // Fatura toggle (detay modal)
  const toggleWDetailInvoice = async (entry) => {
    try {
      if (entry.type === 'debit') {
        await fetch(`${API_URL}/wholesaler-payments/${entry.payment_id}/invoice`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoice_issued: !entry.invoice_issued })
        });
      } else {
        for (const part of entry.parts || [entry]) {
          await fetch(`${API_URL}/wholesaler-payment-history/${part.history_id}/invoice`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ invoice_issued: !entry.invoice_issued })
          });
        }
      }
      fetchPayments();
    } catch (e) {
      toast.error('Fatura durumu güncellenemedi!');
    }
  };

  if (loading) {
    return (
      <div className="wp-container">
        <div className="wp-loading">
          <div className="wp-loading-spinner"></div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const fmtRate = (v, d) => v.toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
  const renderTickerRun = (suffix) => tickerItems.map(it => (
    <span className="wp-ticker-item" key={`${it.key}-${suffix}`}>
      <span className="wp-ticker-label">{it.label}</span>
      {it.alis > 0 && <span className="wp-ticker-alis">{fmtRate(it.alis, it.digits)}</span>}
      <span className="wp-ticker-satis">{fmtRate(it.satis, it.digits)}</span>
      <span className={`wp-ticker-dir ${it.dir > 0 ? 'up' : it.dir < 0 ? 'down' : 'flat'}`}>
        {it.dir > 0 ? '▲' : it.dir < 0 ? '▼' : '•'}
      </span>
    </span>
  ));

  const openOtherGroup = (tab, firstUnpaid) => {
    setSelectedWId(null);
    setShowWDetail(true);
    setWDetailTab(tab);
    if (tab === 'payDebt') {
      setWPayAmount('');
      setWPayMethod('cash');
      setWPayNote('');
      setWPayCurrency(firstUnpaid?.currency || 'TRY');
    }
  };

  const renderMobileHome = () => (
    <WholesalerMobileHome
      summaryPeriod={summaryPeriod} setSummaryPeriod={setSummaryPeriod}
      summaryCustomFrom={summaryCustomFrom} setSummaryCustomFrom={setSummaryCustomFrom}
      summaryCustomTo={summaryCustomTo} setSummaryCustomTo={setSummaryCustomTo}
      summaryStats={summaryStats} liveRates={liveRates} currencyInfo={currencyInfo}
      toTRY={toTRY}
      searchTerm={searchTerm} setSearchTerm={setSearchTerm}
      filterDebtStatus={filterDebtStatus} setFilterDebtStatus={setFilterDebtStatus}
      filterCurrencyType={filterCurrencyType} setFilterCurrencyType={setFilterCurrencyType}
      filterSortBy={filterSortBy} setFilterSortBy={setFilterSortBy}
      wholesalerGroups={wholesalerGroups}
      onOpenSummaryDetail={openSummaryDetail}
      onOpenDetail={openWholesalerDetail}
      onEditWholesaler={openEditWholesaler}
      onNewPayment={() => {
        setStandaloneDebt({ title: '', amount: '', currency: 'USD', due_date: '', photos: [] });
        setShowStandaloneDebt(true);
      }}
      onNewWholesaler={() => {
        setEditingWholesalerData(null);
        setWholesalerFormData({ name: '', company_name: '', phone: '', email: '', address: '', notes: '' });
        setShowWholesalerModal(true);
      }}
      onOpenArchive={() => { setShowArchiveModal(true); setArchiveSearch(''); }}
      onOtherGroup={openOtherGroup}
    />
  );

  return (
    <div className={`wp-container${mobileApp ? ' wp-mobile' : ''}`}>
      {/* Kayan anlık kur bandı (alış / satış) */}
      {tickerItems.length > 0 && (
        <div className="wp-ticker" title="Anlık kurlar (alış / satış)">
          <div className="wp-ticker-track">
            {renderTickerRun('a')}
            {renderTickerRun('b')}
          </div>
        </div>
      )}
      {mobileApp ? renderMobileHome() : (<>
      {/* Header */}
      <header className="wp-header">
        <div className="wp-header-left">
          <button className="wp-back-btn" onClick={() => navigate('/')}>
            <ArrowLeft size={18} /> Ana Sayfa
          </button>
          <h1 className="wp-title">Toptancı Ödeme Takibi</h1>
        </div>
        <div className="wp-header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Logo"
            className="wp-logo"
          />
        </div>
        <div className="wp-header-right">
          <button className="wp-icon-btn" onClick={() => { setShowArchiveModal(true); setArchiveSearch(''); }} title="Arşiv">
            <ScrollText size={20} />
          </button>
          <div style={{ position: 'relative' }}>
            <button className="wp-header-btn secondary" disabled={!!fullReportLoading} onClick={() => setShowFullReportMenu(v => !v)}
              title="Tüm toptancıların borç/alacak kayıtları ve ödeme geçmişi (yedek)">
              <FileDown size={18} /> {fullReportLoading ? 'Hazırlanıyor...' : 'Tam Rapor'}
            </button>
            {showFullReportMenu && (
              <>
                <div onClick={() => setShowFullReportMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 900 }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 901, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 10px 25px rgba(15,23,42,0.15)', padding: '6px', minWidth: '230px' }}>
                  {[['excel', '📊', 'Excel İndir', 'Özet, kayıtlar, ödeme geçmişi, toptancı bilgileri'], ['pdf', '📄', 'PDF İndir', 'Özet + her toptancı için hareket dökümü']].map(([fmt, icon, label, desc]) => (
                    <button key={fmt} type="button" onClick={() => generateFullReport(fmt)}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '9px 10px', border: 'none', background: 'transparent', borderRadius: '7px', cursor: 'pointer', textAlign: 'left' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <span style={{ fontSize: '20px' }}>{icon}</span>
                      <span>
                        <span style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{label}</span>
                        <span style={{ display: 'block', fontSize: '11px', color: '#64748b' }}>{desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button className="wp-header-btn primary" onClick={() => {
            setStandaloneDebt({ title: '', amount: '', currency: 'USD', due_date: '', photos: [] });
            setShowStandaloneDebt(true);
          }}>
            <Coins size={18} /> Yeni Ödeme Ekle
          </button>
          <button className="wp-header-btn primary" onClick={() => {
            setEditingWholesalerData(null);
            setWholesalerFormData({ name: '', company_name: '', phone: '', email: '', address: '', notes: '' });
            setShowWholesalerModal(true);
          }}>
            <Plus size={18} /> Yeni Toptancı
          </button>
        </div>
      </header>

      {/* Özet Zaman Filtresi */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginRight: '4px' }}>Dönem</span>
        {[
          { id: 'current', label: 'Güncel' },
          { id: 'all', label: 'Tüm Zamanlar' },
          { id: 'thisMonth', label: 'Bu Ay' },
          { id: 'lastMonth', label: 'Geçen Ay' },
          { id: 'thisYear', label: 'Bu Yıl' },
          { id: 'custom', label: 'Tarih Aralığı' }
        ].map(opt => {
          const active = summaryPeriod === opt.id;
          return (
            <button key={opt.id} onClick={() => setSummaryPeriod(opt.id)}
              style={{
                padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s',
                background: active ? '#0f766e' : '#fff',
                color: active ? '#fff' : '#475569',
                border: `1px solid ${active ? '#0f766e' : '#cbd5e1'}`,
                boxShadow: active ? '0 1px 3px rgba(15,118,110,0.2)' : 'none'
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#94a3b8'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
            >{opt.label}</button>
          );
        })}
        {summaryPeriod === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
            <input type="date" value={summaryCustomFrom} onChange={e => setSummaryCustomFrom(e.target.value)}
              style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '6px', outline: 'none' }}
            />
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>→</span>
            <input type="date" value={summaryCustomTo} onChange={e => setSummaryCustomTo(e.target.value)}
              style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '6px', outline: 'none' }}
            />
            {(summaryCustomFrom || summaryCustomTo) && (
              <button onClick={() => { setSummaryCustomFrom(''); setSummaryCustomTo(''); }}
                style={{ padding: '4px 8px', fontSize: '11px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
              >Temizle</button>
            )}
          </div>
        )}
        {summaryPeriod !== 'custom' && summaryPeriod !== 'current' && summaryPeriod !== 'all' && (
          <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '4px', fontFamily: 'monospace' }}>
            {summaryDateRange.from && summaryDateRange.to
              ? `${summaryDateRange.from.toLocaleDateString('tr-TR')} — ${summaryDateRange.to.toLocaleDateString('tr-TR')}`
              : ''}
          </span>
        )}
      </div>

      {/* Özet Tablosu - Excel görünümü */}
      {(() => {
        const currencies = ['TRY', 'USD', 'EUR', 'GOLD', 'SILVER'];
        const currShortSum = { TRY: 'TL', USD: 'Dolar', EUR: 'Euro', GOLD: 'Has Altın', SILVER: 'Has Gümüş' };
        const fmt = (v) => Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const numFont = { fontFamily: '"SF Mono","Menlo","Consolas",monospace', fontVariantNumeric: 'tabular-nums' };
        const cellBorder = '1px solid #e5e7eb';
        const thSum = {
          padding: '9px 12px', fontSize: '11px', fontWeight: 700, color: '#1e293b',
          textTransform: 'uppercase', letterSpacing: '0.4px',
          background: '#e2e8f0', borderBottom: '2px solid #94a3b8',
          borderRight: cellBorder, whiteSpace: 'nowrap'
        };
        const tdSum = { padding: '8px 12px', borderBottom: cellBorder, borderRight: cellBorder, fontSize: '13px', whiteSpace: 'nowrap' };

        return (
          <div style={{ background: '#fff', borderRadius: '10px', overflow: 'hidden', border: '1px solid #cbd5e1', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thSum, textAlign: 'left', paddingLeft: '16px' }}>Birim</th>
                  <th style={{ ...thSum, textAlign: 'right', color: '#b91c1c' }}>Toplam Borç</th>
                  <th style={{ ...thSum, textAlign: 'right', color: '#15803d' }}>Ödenen</th>
                  <th style={{ ...thSum, textAlign: 'right', color: '#b91c1c' }}>Kalan Borç</th>
                  <th style={{ ...thSum, textAlign: 'right', color: '#15803d' }}>Alacak</th>
                  <th style={{ ...thSum, textAlign: 'right' }}>Net</th>
                  <th style={{ ...thSum, textAlign: 'right', color: '#c2410c', borderRight: 'none', paddingRight: '16px' }}>Gecikmiş</th>
                </tr>
              </thead>
              <tbody>
                {currencies.map((curr, idx) => {
                  const d = summaryStats.byCurrency[curr] || { total: 0, paid: 0, remaining: 0, credit: 0, overdue: 0 };
                  const net = (d.remaining || 0) - (d.credit || 0);
                  const isEmpty = !d.total && !d.credit;
                  const rowBg = idx % 2 === 0 ? '#ffffff' : '#f8fafc';
                  return (
                    <tr key={`sum-${curr}`} style={{ background: rowBg, opacity: isEmpty ? 0.55 : 1 }}>
                      <td style={{ ...tdSum, paddingLeft: '16px', fontWeight: 700 }}>
                        <span style={{ fontSize: '12px', padding: '3px 10px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '3px', color: '#1e293b', fontWeight: 700 }}>
                          {currencyInfo[curr]?.icon} {currShortSum[curr]}
                        </span>
                      </td>
                      <td style={{ ...tdSum, ...numFont, textAlign: 'right', fontWeight: 700, color: d.total > 0 ? '#1e293b' : '#cbd5e1' }}>{d.total > 0 ? fmt(d.total) : '—'}</td>
                      <td style={{ ...tdSum, ...numFont, textAlign: 'right', fontWeight: 700, color: d.paid > 0 ? '#15803d' : '#cbd5e1', background: d.paid > 0 ? 'rgba(34,197,94,0.04)' : undefined }}>{d.paid > 0 ? fmt(d.paid) : '—'}</td>
                      <td onClick={() => d.remaining > 0 && openSummaryDetail('debt')} style={{ ...tdSum, ...numFont, textAlign: 'right', fontWeight: 700, color: d.remaining > 0 ? '#b91c1c' : '#cbd5e1', background: d.remaining > 0 ? 'rgba(239,68,68,0.04)' : undefined, cursor: d.remaining > 0 ? 'pointer' : 'default' }}>{d.remaining > 0 ? fmt(d.remaining) : '—'}</td>
                      <td onClick={() => d.credit > 0 && openSummaryDetail('credit')} style={{ ...tdSum, ...numFont, textAlign: 'right', fontWeight: 700, color: d.credit > 0 ? '#15803d' : '#cbd5e1', background: d.credit > 0 ? 'rgba(34,197,94,0.04)' : undefined, cursor: d.credit > 0 ? 'pointer' : 'default' }}>{d.credit > 0 ? fmt(d.credit) : '—'}</td>
                      <td style={{ ...tdSum, textAlign: 'right', fontWeight: 800 }}>
                        {Math.abs(net) < 0.0001 ? <span style={{ color: '#94a3b8', ...numFont }}>—</span> : (
                          <span style={{
                            padding: '3px 10px', borderRadius: '4px', ...numFont,
                            background: net > 0 ? '#fee2e2' : '#dcfce7',
                            border: `1px solid ${net > 0 ? '#fca5a5' : '#86efac'}`,
                            color: net > 0 ? '#991b1b' : '#166534'
                          }}>
                            {fmt(Math.abs(net))}
                            <span style={{ fontSize: '10px', marginLeft: '4px', opacity: 0.8 }}>{net > 0 ? 'Borç' : 'Alacak'}</span>
                            {curr !== 'TRY' && liveRates[curr] > 0 && (
                              <span style={{ fontSize: '10px', marginLeft: '4px', fontWeight: 600, opacity: 0.75 }}>(₺{fmt(Math.abs(net) * liveRates[curr])})</span>
                            )}
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdSum, ...numFont, textAlign: 'right', fontWeight: 700, color: d.overdue > 0 ? '#c2410c' : '#cbd5e1', borderRight: 'none', paddingRight: '16px' }}>{d.overdue > 0 ? fmt(d.overdue) : '—'}</td>
                    </tr>
                  );
                })}
                {(() => {
                  const tt = summaryStats.totalTotalTRY;
                  const dt = summaryStats.totalDebtTRY;
                  const pt = summaryStats.totalPaidTRY;
                  const ct = summaryStats.totalCreditTRY;
                  const ot = summaryStats.totalOverdueTRY;
                  const netT = dt - ct;
                  return (
                    <tr style={{ background: '#f1f5f9', borderTop: '2px solid #94a3b8' }}>
                      <td style={{ ...tdSum, paddingLeft: '16px', fontWeight: 800, fontSize: '12px', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Toplam (₺)</td>
                      <td style={{ ...tdSum, ...numFont, textAlign: 'right', fontWeight: 800, color: '#1e293b' }}>₺{fmt(tt)}</td>
                      <td style={{ ...tdSum, ...numFont, textAlign: 'right', fontWeight: 800, color: '#15803d' }}>₺{fmt(pt)}</td>
                      <td style={{ ...tdSum, ...numFont, textAlign: 'right', fontWeight: 800, color: '#b91c1c' }}>₺{fmt(dt)}</td>
                      <td style={{ ...tdSum, ...numFont, textAlign: 'right', fontWeight: 800, color: '#15803d' }}>₺{fmt(ct)}</td>
                      <td style={{ ...tdSum, textAlign: 'right', fontWeight: 800 }}>
                        {Math.abs(netT) < 0.01 ? <span style={{ color: '#94a3b8', ...numFont }}>—</span> : (
                          <span style={{
                            padding: '3px 10px', borderRadius: '4px', ...numFont,
                            background: netT > 0 ? '#fee2e2' : '#dcfce7',
                            border: `1px solid ${netT > 0 ? '#fca5a5' : '#86efac'}`,
                            color: netT > 0 ? '#991b1b' : '#166534'
                          }}>
                            ₺{fmt(Math.abs(netT))}
                            <span style={{ fontSize: '10px', marginLeft: '4px', opacity: 0.8 }}>{netT > 0 ? 'Borç' : 'Alacak'}</span>
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdSum, ...numFont, textAlign: 'right', fontWeight: 800, color: '#c2410c', borderRight: 'none', paddingRight: '16px' }}>₺{fmt(ot)}</td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Arama + Filtre */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Arama */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <Search size={18} color="#94a3b8" />
            <input type="text" placeholder="Toptancı veya ödeme ara..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: '14px', background: 'transparent', color: '#1e293b' }}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '18px' }}>×</button>
            )}
            <span style={{ fontSize: '12px', color: '#94a3b8', flexShrink: 0 }}>{wholesalerGroups.length} toptancı</span>
          </div>
          {/* Filtre butonu */}
          <button onClick={() => setShowFilterPanel(!showFilterPanel)}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px', padding: '10px 14px',
              background: showFilterPanel ? '#0f766e' : '#fff', color: showFilterPanel ? '#fff' : '#475569',
              border: `1px solid ${showFilterPanel ? '#0f766e' : '#e2e8f0'}`, borderRadius: '12px',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0
            }}
          >
            <SlidersHorizontal size={16} /> Filtre
            {(filterDebtStatus !== 'all' || filterCurrencyType !== 'all') && (
              <span style={{
                width: '18px', height: '18px', borderRadius: '50%', fontSize: '10px', fontWeight: 700,
                background: showFilterPanel ? 'rgba(255,255,255,0.3)' : '#0f766e', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {[filterDebtStatus !== 'all', filterCurrencyType !== 'all'].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Filtre Paneli */}
        {showFilterPanel && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', padding: '12px 16px',
            background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', flexWrap: 'wrap'
          }}>
            {/* Borç Durumu */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Durum:</span>
              {[
                { id: 'all', label: 'Tümü' },
                { id: 'hasDebt', label: 'Borçlu' },
                { id: 'noDebt', label: 'Temiz' }
              ].map(opt => (
                <button key={opt.id} onClick={() => setFilterDebtStatus(opt.id)}
                  style={{
                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                    border: filterDebtStatus === opt.id ? '1px solid #0f766e' : '1px solid #e2e8f0',
                    background: filterDebtStatus === opt.id ? '#f0fdfa' : '#fff',
                    color: filterDebtStatus === opt.id ? '#0f766e' : '#64748b', transition: 'all 0.15s'
                  }}
                >{opt.label}</button>
              ))}
            </div>

            <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }}></div>

            {/* Para Birimi */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Birim:</span>
              <button onClick={() => setFilterCurrencyType('all')}
                style={{
                  padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                  border: filterCurrencyType === 'all' ? '1px solid #0f766e' : '1px solid #e2e8f0',
                  background: filterCurrencyType === 'all' ? '#f0fdfa' : '#fff',
                  color: filterCurrencyType === 'all' ? '#0f766e' : '#64748b', transition: 'all 0.15s'
                }}
              >Tümü</button>
              {Object.entries(currencyInfo).map(([code, info]) => (
                <button key={code} onClick={() => setFilterCurrencyType(code)}
                  style={{
                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                    border: filterCurrencyType === code ? '1px solid #0f766e' : '1px solid #e2e8f0',
                    background: filterCurrencyType === code ? '#f0fdfa' : '#fff',
                    color: filterCurrencyType === code ? '#0f766e' : '#64748b', transition: 'all 0.15s'
                  }}
                >{info.icon} {code}</button>
              ))}
            </div>

            <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }}></div>

            {/* Sıralama */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Sırala:</span>
              <select value={filterSortBy} onChange={e => setFilterSortBy(e.target.value)}
                style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '11px', background: '#fff', color: '#475569', fontWeight: 600 }}
              >
                <option value="debt_desc">Borç (Büyük→Küçük)</option>
                <option value="debt_asc">Borç (Küçük→Büyük)</option>
                <option value="name_asc">İsim (A→Z)</option>
                <option value="name_desc">İsim (Z→A)</option>
              </select>
            </div>

            {/* Temizle */}
            {(filterDebtStatus !== 'all' || filterCurrencyType !== 'all' || filterSortBy !== 'debt_desc') && (
              <>
                <div style={{ flex: 1 }}></div>
                <button onClick={() => { setFilterDebtStatus('all'); setFilterCurrencyType('all'); setFilterSortBy('debt_desc'); }}
                  style={{
                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                    background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', cursor: 'pointer'
                  }}
                >Temizle</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Toptancı Listesi - Başlık */}
      <div className="wp-payment-list">
        {wholesalerGroups.length === 0 ? (
          <div className="wp-empty">
            <Sparkles size={48} />
            <p>{searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz toptancı yok'}</p>
            {!searchTerm && (
              <button className="wp-reset-btn" onClick={() => {
                setEditingWholesalerData(null);
                setWholesalerFormData({ name: '', company_name: '', phone: '', email: '', address: '', notes: '' });
                setShowWholesalerModal(true);
              }}>
                <Plus size={14} /> Toptancı Ekle
              </button>
            )}
          </div>
        ) : (
          <>
          {/* Tablo Başlık Satırı - kartlarla aynı flex yapısı */}
          <div style={{
            display: 'flex', alignItems: 'center', padding: '8px 16px', marginBottom: '6px',
            background: '#f1f5f9', borderRadius: '10px', border: '1px solid #e2e8f0', gap: '12px'
          }}>
            <div style={{ width: '42px', flexShrink: 0 }}></div>
            <div style={{ flex: 1, fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Toptancı / Firma</div>
            <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0', flexShrink: 0 }}></div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Kalan Borç</span>
            </div>
            <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0', flexShrink: 0 }}></div>
            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: '4px' }}>
              <span style={{ padding: '8px 14px', fontSize: '11px', fontWeight: 700, color: '#0f766e', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}><Plus size={14} style={{ verticalAlign: 'middle', marginRight: '2px', marginTop: '-1px', opacity: 0 }} />Borç</span>
              <span style={{ padding: '8px 14px', fontSize: '11px', fontWeight: 700, color: '#dc2626', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px', border: '1px solid transparent' }}><Coins size={14} style={{ verticalAlign: 'middle', marginRight: '2px', marginTop: '-1px', opacity: 0 }} />Ödeme</span>
              <span style={{ padding: '8px 14px', fontSize: '11px', fontWeight: 700, color: '#475569', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px', border: '1px solid transparent' }}><Pencil size={13} style={{ verticalAlign: 'middle', marginRight: '2px', marginTop: '-1px', opacity: 0 }} />Düzenle</span>
              <span style={{ padding: '8px 14px', fontSize: '11px', fontWeight: 700, color: '#475569', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px', border: '1px solid transparent' }}><ScrollText size={13} style={{ verticalAlign: 'middle', marginRight: '2px', marginTop: '-1px', opacity: 0 }} />Geçmiş</span>
            </div>
          </div>
          {wholesalerGroups.map((group, gIdx) => {
            const initials = group.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
            const firstUnpaid = group.payments.find(p => (p.status === 'pending' || p.status === 'partial') && p.payment_type !== 'income');
            const hasDebt = Object.values(group.totalByCurrency).some(d => d.remaining > 0);

            return (
              <div key={group.key}>
                {/* Toptancı Kartı - tek satır, tüm butonlar */}
                <div
                  style={{
                    display: 'flex', alignItems: 'center', padding: '12px 16px',
                    background: gIdx % 2 === 0 ? '#ffffff' : '#f8fafb',
                    border: '1px solid #e2e8f0', borderRadius: '12px',
                    marginBottom: '5px',
                    boxShadow: gIdx % 2 === 0 ? '0 1px 3px rgba(0,0,0,0.04)' : '0 1px 2px rgba(0,0,0,0.02)',
                    transition: 'all 0.15s', gap: '12px'
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: '42px', height: '42px', borderRadius: '50%',
                    background: group.wholesaler_id ? 'linear-gradient(135deg, #0f766e, #14b8a6)' : 'linear-gradient(135deg, #64748b, #94a3b8)',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '14px', fontWeight: 700, flexShrink: 0
                  }}>
                    {initials}
                  </div>

                  {/* İsim ve bilgi */}
                  <div style={{ flex: 1, minWidth: 0, cursor: group.wholesaler_id ? 'pointer' : 'default' }}
                    onClick={() => { if (group.wholesaler_id) openWholesalerDetail(group.wholesaler_id); }}
                  >
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
                      {group.name}
                      {group.company_name && group.company_name !== group.name && (
                        <span style={{ fontSize: '12px', fontWeight: 400, color: '#94a3b8', marginLeft: '6px' }}>({group.company_name})</span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                      {group.payments.length === 0 ? 'Borç yok' : `${group.payments.length} borç kaydı`}
                      {Object.entries(group.totalByCurrency).map(([curr, data]) => (
                        data.paid > 0 ? <span key={curr} style={{ color: '#22c55e' }}> · {formatCurrency(data.paid, curr)} ödendi</span> : null
                      ))}
                    </div>
                  </div>

                  {/* Dikey çizgi */}
                  <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0', flexShrink: 0 }}></div>

                  {/* Toplam kalan borç / alacak - her birime tıklanabilir */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, gap: '2px' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                    {Object.entries(group.totalByCurrency).map(([curr, data]) => {
                      const unpaidForCurr = group.payments.find(p => (p.status === 'pending' || p.status === 'partial') && (p.currency || 'TRY') === curr);
                      const net = data.net || 0;
                      if (Math.abs(net) < 0.0001) return null;
                      const isCredit = net < 0;
                      const displayAmt = Math.abs(net);
                      // Her birim için ayrı renk paleti. Tone: alacak (credit) = açık dolgu,
                      // verecek (debit) = koyu dolgu — birimi renk, durumu ton ile ayırt et.
                      const palettes = {
                        TRY:    { credit: { bg: '#f1f5f9', bgHover: '#e2e8f0', border: '#cbd5e1', color: '#334155' }, debit: { bg: '#334155', bgHover: '#1e293b', border: '#334155', color: '#ffffff' } },
                        USD:    { credit: { bg: '#ecfdf5', bgHover: '#d1fae5', border: '#6ee7b7', color: '#047857' }, debit: { bg: '#047857', bgHover: '#065f46', border: '#047857', color: '#ffffff' } },
                        EUR:    { credit: { bg: '#eff6ff', bgHover: '#dbeafe', border: '#93c5fd', color: '#1d4ed8' }, debit: { bg: '#1d4ed8', bgHover: '#1e40af', border: '#1d4ed8', color: '#ffffff' } },
                        GOLD:   { credit: { bg: '#fffbeb', bgHover: '#fef3c7', border: '#fcd34d', color: '#b45309' }, debit: { bg: '#b45309', bgHover: '#92400e', border: '#b45309', color: '#ffffff' } },
                        SILVER: { credit: { bg: '#faf5ff', bgHover: '#f3e8ff', border: '#d8b4fe', color: '#6b21a8' }, debit: { bg: '#6b21a8', bgHover: '#581c87', border: '#6b21a8', color: '#ffffff' } },
                      };
                      const pal = palettes[curr] || palettes.TRY;
                      const tone = isCredit ? pal.credit : pal.debit;
                      const subColor = isCredit ? '#94a3b8' : 'rgba(255,255,255,0.75)';
                      const label = isCredit ? 'alacak' : 'verecek';
                      return (
                        <span key={curr}
                          onClick={() => { if (group.wholesaler_id) openWholesalerDetail(group.wholesaler_id, 'payDebt', curr); }}
                          title={unpaidForCurr ? `${currencyInfo[curr]?.name || curr} borcuna ödeme yap` : (isCredit ? `${currencyInfo[curr]?.name || curr} alacağınız var` : `${currencyInfo[curr]?.name || curr} vereceğiniz var`)}
                          style={{
                            padding: '4px 14px', background: tone.bg, border: `1px solid ${tone.border}`,
                            borderRadius: '8px', fontSize: '15px', fontWeight: 700, color: tone.color,
                            cursor: unpaidForCurr ? 'pointer' : 'default', transition: 'all 0.15s'
                          }}
                          onMouseEnter={e => { if (unpaidForCurr) { e.currentTarget.style.background = tone.bgHover; e.currentTarget.style.transform = 'scale(1.05)'; } }}
                          onMouseLeave={e => { e.currentTarget.style.background = tone.bg; e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                          <div>{formatCurrency(displayAmt, curr)}<span style={{ fontSize: '10px', marginLeft: '4px', fontWeight: 600, opacity: 0.85 }}>{label}</span></div>
                          {toTRY(displayAmt, curr) !== null && (
                            <div style={{ fontSize: '10px', fontWeight: 500, color: subColor, marginTop: '1px' }}>₺{Number(toTRY(displayAmt, curr)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          )}
                        </span>
                      );
                    })}
                    </div>
                  </div>

                  {/* Dikey çizgi */}
                  <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0', flexShrink: 0 }}></div>

                  {/* Aksiyon butonları */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, gap: '2px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {(() => {
                      const btn = { padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', textAlign: 'center' };
                      return (<>
                          <button onClick={() => {
                            if (group.wholesaler_id) {
                              openWholesalerDetail(group.wholesaler_id, 'addDebt');
                            } else {
                              setStandaloneDebt({ title: '', amount: '', currency: 'USD', due_date: '', photos: [] });
                              setShowStandaloneDebt(true);
                            }
                          }}
                            style={{ ...btn, background: '#0f766e', color: 'white', border: 'none' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#115e59'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#0f766e'; }}
                          ><Plus size={14} style={{ verticalAlign: 'middle', marginRight: '2px', marginTop: '-1px' }} />Borç</button>

                          {group.wholesaler_id && (
                            <button onClick={() => openWholesalerDetail(group.wholesaler_id, 'addCredit')}
                              style={{ ...btn, background: '#15803d', color: 'white', border: 'none' }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#166534'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#15803d'; }}
                              title="Bu toptancıya alacak ekle"
                            ><Plus size={14} style={{ verticalAlign: 'middle', marginRight: '2px', marginTop: '-1px' }} />Alacak</button>
                          )}

                          <button
                            onClick={() => {
                              if (!firstUnpaid) return;
                              if (group.wholesaler_id) {
                                openWholesalerDetail(group.wholesaler_id, 'payDebt');
                              } else {
                                // Diğer grubu için direkt ödeme
                                setSelectedWId(null);
                                setShowWDetail(true);
                                setWDetailTab('payDebt');
                                setWPayAmount('');
                                setWPayMethod('cash');
                                setWPayNote('');
                                setWPayCurrency(firstUnpaid.currency || 'TRY');
                              }
                            }}
                            disabled={!firstUnpaid}
                            style={{ ...btn, background: firstUnpaid ? '#fef2f2' : '#f8fafc', color: firstUnpaid ? '#dc2626' : '#cbd5e1', border: `1px solid ${firstUnpaid ? '#fecaca' : '#e2e8f0'}`, cursor: firstUnpaid ? 'pointer' : 'not-allowed', opacity: firstUnpaid ? 1 : 0.6 }}
                            onMouseEnter={e => { if (firstUnpaid) e.currentTarget.style.background = '#fee2e2'; }}
                            onMouseLeave={e => { if (firstUnpaid) e.currentTarget.style.background = '#fef2f2'; }}
                          ><Coins size={14} style={{ verticalAlign: 'middle', marginRight: '2px', marginTop: '-1px' }} />Ödeme</button>

                        {group.wholesaler_id ? (
                          <button onClick={() => openEditWholesaler(group.wholesaler)}
                            style={{ ...btn, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                          ><Pencil size={13} style={{ verticalAlign: 'middle', marginRight: '2px', marginTop: '-1px' }} />Düzenle</button>
                        ) : (
                          <button onClick={() => {
                            setSelectedWId(null);
                            setShowWDetail(true);
                            setWDetailTab('history');
                          }}
                            style={{ ...btn, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                          ><Pencil size={13} style={{ verticalAlign: 'middle', marginRight: '2px', marginTop: '-1px' }} />Düzenle</button>
                        )}

                          <button onClick={() => {
                            if (group.wholesaler_id) {
                              openWholesalerDetail(group.wholesaler_id, 'history');
                            } else {
                              setSelectedWId(null);
                              setShowWDetail(true);
                              setWDetailTab('history');
                            }
                          }}
                            style={{ ...btn, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                          ><ScrollText size={13} style={{ verticalAlign: 'middle', marginRight: '2px', marginTop: '-1px' }} />Geçmiş</button>

                      </>);
                    })()}
                  </div>
                </div>

                </div>
              </div>
            );
          })}
          </>
        )}
      </div>
      </>)}

      {/* Standalone Borç Ekleme Modal */}
      {showStandaloneDebt && (() => {
        const debtNum = Number(standaloneDebt.amount) || 0;
        const lbl = { fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' };
        const inp = { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
        return (
          <div className="wp-modal-overlay">
            <div className="wp-modal" style={{ maxWidth: '480px' }}>
              <div className="wp-modal-header">
                <h2>Yeni Ödeme Ekle</h2>
                <button className="wp-modal-close" onClick={() => setShowStandaloneDebt(false)}>×</button>
              </div>
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={lbl}>Açıklama *</label>
                    <input type="text" placeholder="Borç açıklaması..." value={standaloneDebt.title}
                      onChange={e => setStandaloneDebt(p => ({ ...p, title: e.target.value }))} style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Birim</label>
                    <select value={standaloneDebt.currency} onChange={e => setStandaloneDebt(p => ({ ...p, currency: e.target.value }))}
                      style={{ ...inp, background: '#fff' }}>
                      {Object.entries(currencyInfo).map(([code, info]) => (
                        <option key={code} value={code}>{info.icon} {info.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <label style={lbl}>Tutar ({currencyInfo[standaloneDebt.currency]?.symbol}) *</label>
                    <PriceInput placeholder="0.00" value={standaloneDebt.amount}
                      onChange={val => setStandaloneDebt(p => ({ ...p, amount: val }))}
                      style={{ ...inp, fontFamily: 'monospace', fontSize: '15px' }} />
                    {debtNum > 0 && toTRY(debtNum, standaloneDebt.currency) !== null && (
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', textAlign: 'right' }}>
                        {formatCurrency(debtNum, standaloneDebt.currency)} = <strong style={{ color: '#0f766e' }}>₺{Number(toTRY(debtNum, standaloneDebt.currency)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={lbl}>Vade Tarihi</label>
                    <input type="date" value={standaloneDebt.due_date}
                      onChange={e => setStandaloneDebt(p => ({ ...p, due_date: e.target.value }))} style={inp} />
                  </div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={lbl}>Fotoğraflar</label>
                  {renderWsPhotoPicker(standaloneDebt.photos, photos => setStandaloneDebt(p => ({ ...p, photos })), 'wp-standalone-photo')}
                </div>
                <button onClick={handleStandaloneAddDebt}
                  disabled={!standaloneDebt.title || debtNum <= 0}
                  style={{
                    width: '100%', padding: '11px', border: 'none', borderRadius: '8px',
                    fontSize: '14px', fontWeight: 700, cursor: (standaloneDebt.title && debtNum > 0) ? 'pointer' : 'not-allowed',
                    background: (standaloneDebt.title && debtNum > 0) ? '#0f766e' : '#d1d5db', color: 'white',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => { if (standaloneDebt.title && debtNum > 0) e.currentTarget.style.background = '#115e59'; }}
                  onMouseLeave={e => { if (standaloneDebt.title && debtNum > 0) e.currentTarget.style.background = '#0f766e'; }}
                >
                  {debtNum > 0 ? `${formatCurrency(debtNum, standaloneDebt.currency)} Borç Ekle` : 'Borç Ekle'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Ödeme Modal */}
      {showModal && (
        <div className={`wp-modal-overlay ${closingModal === 'main' ? 'closing' : ''}`} style={{ zIndex: 1100 }}>
          <div className="wp-modal" onClick={e => e.stopPropagation()}>
            <div className="wp-modal-header">
              <h2>{editingPayment ? 'Ödeme Düzenle' : 'Yeni Toptancı Ödemesi'}</h2>
              <button className="wp-modal-close" onClick={() => handleCloseModal('main', setShowModal)}>×</button>
            </div>

            <div className="wp-modal-body">
              <div className="wp-form-row">
                <div className="wp-form-group">
                  <label>Başlık *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                    placeholder="Ödeme başlığı..."
                  />
                </div>
                <div className="wp-form-group">
                  <label>Tutar *</label>
                  <div className="wp-amount-input">
                    <PriceInput
                      value={formData.amount}
                      onChange={val => setFormData({...formData, amount: val})}
                      placeholder="0.00"
                    />
                    <select
                      value={formData.currency}
                      onChange={e => setFormData({...formData, currency: e.target.value})}
                    >
                      {Object.entries(currencyInfo).map(([code, info]) => (
                        <option key={code} value={code}>{info.icon} {code}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="wp-form-row">
                <div className="wp-form-group">
                  <label>Tür</label>
                  <div className="wp-type-buttons">
                    <button
                      className={`wp-type-btn expense ${formData.payment_type === 'expense' ? 'active' : ''}`}
                      onClick={() => setFormData({...formData, payment_type: 'expense'})}
                    >
                      Borç
                    </button>
                    <button
                      className={`wp-type-btn income ${formData.payment_type === 'income' ? 'active' : ''}`}
                      onClick={() => setFormData({...formData, payment_type: 'income'})}
                    >
                      Alacak
                    </button>
                  </div>
                </div>
                <div className="wp-form-group">
                  <label>Kategori</label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData({...formData, category: e.target.value})}
                  >
                    {Object.entries(categoryNames).map(([key, name]) => (
                      <option key={key} value={key}>{categoryIcons[key]} {name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="wp-form-row">
                <div className="wp-form-group">
                  <label>Toptancı Seç</label>
                  <select
                    value={formData.wholesaler_id}
                    onChange={e => handleWholesalerChange(e.target.value)}
                  >
                    <option value="">-- Toptancı Seçin --</option>
                    {wholesalers.map(w => (
                      <option key={w.id} value={w.id}>
                        {w.name} {w.company_name ? `(${w.company_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="wp-form-group">
                  <label>Firma / Alıcı Adı</label>
                  <input
                    type="text"
                    value={formData.recipient}
                    onChange={e => setFormData({...formData, recipient: e.target.value})}
                    placeholder="Toptancı veya firma adı..."
                  />
                </div>
              </div>

              <div className="wp-form-row">
                <div className="wp-form-group">
                  <label>Vade Tarihi</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={e => setFormData({...formData, due_date: e.target.value})}
                  />
                </div>
                <div className="wp-form-group">
                  <label>Hatırlatma</label>
                  <select
                    value={formData.reminder_days}
                    onChange={e => setFormData({...formData, reminder_days: Number(e.target.value)})}
                  >
                    <option value={1}>1 gün önce</option>
                    <option value={3}>3 gün önce</option>
                    <option value={7}>1 hafta önce</option>
                    <option value={14}>2 hafta önce</option>
                  </select>
                </div>
              </div>

              <div className="wp-form-row">
                <div className="wp-form-group">
                  <label>Durum</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="pending">Bekliyor</option>
                    <option value="partial">Kısmi Ödeme</option>
                    <option value="paid">Ödendi</option>
                    <option value="cancelled">İptal</option>
                  </select>
                </div>
                <div className="wp-form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.recurring}
                      onChange={e => setFormData({...formData, recurring: e.target.checked})}
                    />
                    <span>Tekrarlayan Ödeme</span>
                  </label>
                  {formData.recurring && (
                    <select
                      value={formData.recurring_interval}
                      onChange={e => setFormData({...formData, recurring_interval: e.target.value})}
                    >
                      <option value="weekly">Haftalık</option>
                      <option value="monthly">Aylık</option>
                      <option value="yearly">Yıllık</option>
                    </select>
                  )}
                </div>
              </div>

              {formData.recurring && formData.due_date && (
                <div className="wp-next-occurrence">
                  <RefreshCw size={16} />
                  <span>Sonraki ödeme: <strong>
                    {getNextOccurrence(formData.due_date, formData.recurring_interval).toLocaleDateString('tr-TR', {
                      day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </strong></span>
                </div>
              )}

              <div className="wp-form-group">
                <label>Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  placeholder="Ödeme detayları..."
                  rows="2"
                />
              </div>

              <div className="wp-form-group">
                <label>Notlar</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({...formData, notes: e.target.value})}
                  placeholder="Ek notlar..."
                  rows="2"
                />
              </div>

              <div className="wp-form-group">
                <label>Fotoğraflar</label>
                {renderWsPhotoPicker(formData.photos, photos => setFormData(prev => ({ ...prev, photos })), 'wp-main-photo')}
              </div>

              <div className="wp-form-group checkbox invoice">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.invoice_issued}
                    onChange={e => setFormData({...formData, invoice_issued: e.target.checked})}
                  />
                  <FileText size={18} className={formData.invoice_issued ? 'active' : ''} />
                  <span>Fatura Kesildi</span>
                </label>
              </div>
            </div>

            <div className="wp-modal-footer">
              <button className="wp-btn cancel" onClick={() => setShowModal(false)}>İptal</button>
              <button className="wp-btn save" onClick={handleSave}>
                {editingPayment ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ödeme Detay Modal */}
      {showDetailModal && detailPayment && (() => {
        const total = Number(detailPayment.amount) || 0;
        const isPaidDetail = detailPayment.status === 'paid';
        const paidDetail = isPaidDetail ? total : (Number(detailPayment.paid_amount) || 0);
        const remainingDetail = isPaidDetail ? 0 : Math.max(0, total - paidDetail);
        const pctDetail = isPaidDetail ? 100 : (total > 0 ? Math.min(100, Math.round((paidDetail / total) * 100)) : 0);
        return (
        <div className="wp-modal-overlay">
          <div className="wp-modal" style={{ maxWidth: '520px' }}>
            <div className="wp-modal-header">
              <h3>{detailPayment.recipient || categoryNames[detailPayment.category]} — {detailPayment.title}</h3>
              <button className="wp-modal-close" onClick={() => setShowDetailModal(false)}><X size={20} /></button>
            </div>
            <div className="wp-modal-body" style={{ padding: '16px 20px' }}>
              {/* Özet */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', padding: '12px 14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>Toplam Borç</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(total, detailPayment.currency)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>Ödenen</div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#22c55e' }}>{formatCurrency(paidDetail, detailPayment.currency)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>Kalan</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: remainingDetail > 0 ? '#ef4444' : '#22c55e' }}>{formatCurrency(remainingDetail, detailPayment.currency)}</div>
                </div>
              </div>

              {/* Progress */}
              <div style={{ marginBottom: '14px' }}>
                <div className="wp-card-progress-bar" style={{ height: '6px' }}>
                  <div className="wp-card-progress-fill" style={{ width: `${pctDetail}%` }} />
                </div>
                <div style={{ textAlign: 'center', fontSize: '11px', color: '#64748b', marginTop: '4px' }}>%{pctDetail} ödendi</div>
              </div>

              {/* Bilgiler */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px', fontSize: '12px' }}>
                <div style={{ padding: '8px 10px', background: '#f1f5f9', borderRadius: '8px' }}>
                  <span style={{ color: '#64748b' }}>Durum: </span>
                  <span className={`wp-status-badge ${detailPayment.status}`} style={{ fontSize: '10px' }}>{statusNames[detailPayment.status]}</span>
                </div>
                <div style={{ padding: '8px 10px', background: '#f1f5f9', borderRadius: '8px' }}>
                  <span style={{ color: '#64748b' }}>Tür: </span>
                  <strong>{detailPayment.payment_type === 'expense' ? 'Borç' : 'Alacak'}</strong>
                </div>
                <div style={{ padding: '8px 10px', background: '#f1f5f9', borderRadius: '8px' }}>
                  <span style={{ color: '#64748b' }}>Kategori: </span>
                  <strong>{categoryNames[detailPayment.category]}</strong>
                </div>
                {detailPayment.payment_method && (
                  <div style={{ padding: '8px 10px', background: '#f1f5f9', borderRadius: '8px' }}>
                    <span style={{ color: '#64748b' }}>Ödeme: </span>
                    <strong>{paymentMethodInfo[detailPayment.payment_method]?.name || detailPayment.payment_method}</strong>
                  </div>
                )}
                {detailPayment.due_date && (
                  <div style={{ padding: '8px 10px', background: '#f1f5f9', borderRadius: '8px' }}>
                    <span style={{ color: '#64748b' }}>Vade: </span>
                    <strong>{new Date(detailPayment.due_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
                  </div>
                )}
                <div style={{ padding: '8px 10px', background: '#f1f5f9', borderRadius: '8px' }}>
                  <span style={{ color: '#64748b' }}>Fatura: </span>
                  <strong style={{ color: detailPayment.invoice_issued ? '#22c55e' : '#94a3b8' }}>{detailPayment.invoice_issued ? 'Kesildi ✓' : 'Kesilmedi'}</strong>
                </div>
              </div>

              {detailPayment.notes && (
                <div style={{ padding: '8px 10px', background: '#fffbeb', borderRadius: '8px', fontSize: '12px', color: '#92400e', marginBottom: '14px' }}>
                  <strong>Not:</strong> {detailPayment.notes}
                </div>
              )}

              {/* Aksiyonlar */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {(detailPayment.status === 'pending' || detailPayment.status === 'partial') && (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      className="wp-action-text-btn partial"
                      style={{ flex: 1, justifyContent: 'center', padding: '8px', fontSize: '13px' }}
                      onClick={() => { setShowDetailModal(false); openPartialModal(detailPayment); }}
                    >
                      <Coins size={15} /> Kısmi Ödeme
                    </button>
                    <button
                      className="wp-action-text-btn paid"
                      style={{ flex: 1, justifyContent: 'center', padding: '8px', fontSize: '13px' }}
                      onClick={() => { setShowDetailModal(false); openPaidModal(detailPayment); }}
                    >
                      <Check size={15} /> Ödendi
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    className={`wp-action-text-btn invoice ${detailPayment.invoice_issued ? 'active' : ''}`}
                    style={{ flex: 1, justifyContent: 'center', padding: '7px', fontSize: '12px' }}
                    onClick={() => { toggleInvoiceStatus(detailPayment); setShowDetailModal(false); }}
                  >
                    <FileText size={14} /> {detailPayment.invoice_issued ? 'Fatura Kesildi' : 'Fatura Kes'}
                  </button>
                  {detailPayment.status === 'paid' && (
                    <button
                      className="wp-action-text-btn archive"
                      style={{ flex: 1, justifyContent: 'center', padding: '7px', fontSize: '12px' }}
                      onClick={() => { handleArchive(detailPayment.id); setShowDetailModal(false); }}
                    >
                      <ScrollText size={14} /> Arşivle
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    className="wp-action-text-btn edit"
                    style={{ flex: 1, justifyContent: 'center', padding: '7px', fontSize: '12px' }}
                    onClick={() => { setShowDetailModal(false); openEditModal(detailPayment); }}
                  >
                    <Pencil size={14} /> Düzenle
                  </button>
                  <button
                    className="wp-action-text-btn delete"
                    style={{ flex: 1, justifyContent: 'center', padding: '7px', fontSize: '12px' }}
                    onClick={() => { setShowDetailModal(false); handleDelete(detailPayment); }}
                  >
                    <Trash2 size={14} /> Sil
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Kısmi Ödeme Modal */}
      {showPartialModal && partialPayment && (
        <div className={`wp-modal-overlay ${closingModal === 'partial' ? 'closing' : ''}`} style={{ zIndex: 1100 }}>
          <div className="wp-modal partial" onClick={e => e.stopPropagation()}>
            <div className="wp-modal-header">
              <div className="wp-modal-header-info">
                <h2>Kısmi Ödeme</h2>
                <span className="wp-modal-subtitle">{partialPayment.title} · {partialPayment.recipient || categoryNames[partialPayment.category]}</span>
              </div>
              <button className="wp-modal-close" onClick={() => handleCloseModal('partial', setShowPartialModal)}>×</button>
            </div>

            <div className="wp-modal-body">

              {(() => {
                const totalAmt = Number(partialPayment.amount) || 0;
                const prevPaid = Number(partialPayment.paid_amount) || 0;
                const newPay = Number(partialNewAmount) || 0;
                const totalPaid = prevPaid + newPay;
                const liveRemaining = Math.max(0, totalAmt - totalPaid);
                const livePct = totalAmt > 0 ? Math.min(100, Math.round((totalPaid / totalAmt) * 100)) : 0;
                const isComplete = totalPaid >= totalAmt;
                const remainingDebt = totalAmt - prevPaid;
                const methodLabels = { credit_card: 'Kredi Kartı', ...Object.fromEntries(Object.entries(paymentMethodInfo).map(([k, v]) => [k, v.name])) };
                const curr = partialPayment.currency;

                // Geçmiş ödemelerin toplamı
                const historyTotal = paymentHistory.reduce((sum, h) => sum + Number(h.amount), 0);
                // Geçmişte olmayan eski ödeme farkı
                const unrecordedAmount = prevPaid - historyTotal;

                return (
                  <div className="wp-ledger">
                    {/* Başlık - Hesap Özeti */}
                    <div className="wp-ledger-title">
                      <span>Hesap Özeti</span>
                    </div>

                    {/* Tablo Başlıkları */}
                    <div className="wp-ledger-head">
                      <span className="wp-ledger-col-date">Tarih</span>
                      <span className="wp-ledger-col-desc">Açıklama</span>
                      <span className="wp-ledger-col-invoice">Fatura</span>
                      <span className="wp-ledger-col-amount">Tutar</span>
                      <span className="wp-ledger-col-balance">Bakiye</span>
                    </div>

                    {/* Satırlar */}
                    <div className="wp-ledger-body">
                      {/* 1. satır: Borç oluşturma */}
                      {(() => {
                        const createdDate = partialPayment.created_at ? new Date(partialPayment.created_at) : new Date(partialPayment.due_date);
                        return (
                          <div className="wp-ledger-row debit">
                            <span className="wp-ledger-col-date">
                              {createdDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                            </span>
                            <span className="wp-ledger-col-desc">Borç kaydı</span>
                            <span className="wp-ledger-col-invoice">
                              <button
                                className={`wp-invoice-check ${partialPayment.invoice_issued ? 'checked' : ''}`}
                                onClick={(e) => { e.stopPropagation(); toggleInvoiceStatus(partialPayment); }}
                                title={partialPayment.invoice_issued ? 'Fatura kesildi' : 'Fatura kes'}
                              >
                                <FileText size={13} />
                                {partialPayment.invoice_issued ? <Check size={10} className="wp-invoice-tick" /> : null}
                              </button>
                            </span>
                            <span className="wp-ledger-col-amount debit">+{formatCurrency(totalAmt, curr)}</span>
                            <span className="wp-ledger-col-balance">{formatCurrency(totalAmt, curr)}</span>
                          </div>
                        );
                      })()}

                      {/* Geçmişte kaydedilmemiş eski ödeme */}
                      {unrecordedAmount > 0.01 && (
                        <div className="wp-ledger-row credit old">
                          <span className="wp-ledger-col-date">—</span>
                          <span className="wp-ledger-col-desc">Eski ödeme</span>
                          <span className="wp-ledger-col-invoice"></span>
                          <span className="wp-ledger-col-amount credit">-{formatCurrency(unrecordedAmount, curr)}</span>
                          <span className="wp-ledger-col-balance">{formatCurrency(totalAmt - unrecordedAmount, curr)}</span>
                        </div>
                      )}

                      {/* Kayıtlı ödemeler (eskiden yeniye) */}
                      {[...paymentHistory].reverse().map((h, i) => {
                        const hDate = new Date(h.created_at);
                        const dateStr = hDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' });
                        const timeStr = hDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
                        const paidUpToHere = (unrecordedAmount > 0.01 ? unrecordedAmount : 0) +
                          [...paymentHistory].reverse().slice(0, i + 1).reduce((s, x) => s + Number(x.amount), 0);
                        const balanceAfter = totalAmt - paidUpToHere;
                        const isEditing = editingHistoryId === h.id;
                        return (
                          <div
                            key={h.id}
                            className={`wp-ledger-row credit ${isEditing ? 'editing' : ''}`}
                            onDoubleClick={() => {
                              setEditingHistoryId(h.id);
                              setEditingHistoryAmount(String(Number(h.amount)));
                              setEditingHistoryMethod(h.payment_method || 'cash');
                            }}
                            title="Düzenlemek için çift tıklayın"
                          >
                            <span className="wp-ledger-col-date" title={timeStr}>
                              {dateStr}
                              <small>{timeStr}</small>
                            </span>
                            <span className="wp-ledger-col-desc">
                              Ödeme <small>({methodLabels[h.payment_method] || h.payment_method})</small>
                              {h.note && <small style={{ display: 'block', color: '#64748b' }}>{h.note}</small>}
                            </span>
                            <span className="wp-ledger-col-invoice">
                              <button
                                className={`wp-invoice-check ${h.invoice_issued ? 'checked' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Ödeme geçmişi fatura durumunu güncelle
                                  (async () => {
                                    try {
                                      await fetch(`${API_URL}/wholesaler-payment-history/${h.id}/invoice`, {
                                        method: 'PATCH',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ invoice_issued: !h.invoice_issued })
                                      });
                                      setPaymentHistory(prev => prev.map(ph => ph.id === h.id ? { ...ph, invoice_issued: !ph.invoice_issued } : ph));
                                    } catch (err) { console.error('Fatura güncelleme hatası:', err); }
                                  })();
                                }}
                                title={h.invoice_issued ? 'Fatura kesildi' : 'Fatura kes'}
                              >
                                <FileText size={13} />
                                {h.invoice_issued ? <Check size={10} className="wp-invoice-tick" /> : null}
                              </button>
                            </span>
                            {isEditing ? (
                              <span className="wp-ledger-col-amount credit wp-ledger-edit-cell">
                                <PriceInput
                                  className="wp-ledger-edit-input"
                                  value={editingHistoryAmount}
                                  onChange={val => setEditingHistoryAmount(val)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') handleUpdateHistory(h.id, editingHistoryAmount, editingHistoryMethod);
                                    if (e.key === 'Escape') { setEditingHistoryId(null); setEditingHistoryAmount(''); setEditingHistoryMethod(''); }
                                  }}
                                  autoFocus
                                />
                                <select
                                  value={editingHistoryMethod}
                                  onChange={e => setEditingHistoryMethod(e.target.value)}
                                  style={{ marginLeft: '6px', padding: '4px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px', background: '#fff' }}
                                  title="Ödeme yöntemi"
                                >
                                  {Object.entries(methodLabels).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </select>
                                <div className="wp-ledger-edit-actions">
                                  <button className="wp-ledger-edit-save" onClick={() => handleUpdateHistory(h.id, editingHistoryAmount, editingHistoryMethod)}>&#10003;</button>
                                  <button className="wp-ledger-edit-delete" onClick={() => handleDeleteHistory(h.id)}>&#10005;</button>
                                </div>
                              </span>
                            ) : (
                              <span className="wp-ledger-col-amount credit">-{formatCurrency(h.amount, curr)}</span>
                            )}
                            <span className="wp-ledger-col-balance">{formatCurrency(Math.max(0, balanceAfter), curr)}</span>
                          </div>
                        );
                      })}

                      {/* Yeni ödeme girişi (canlı satır) */}
                      {newPay > 0 && (
                        <div className="wp-ledger-row credit new-entry">
                          <span className="wp-ledger-col-date">Yeni</span>
                          <span className="wp-ledger-col-desc">Yeni ödeme</span>
                          <span className="wp-ledger-col-invoice"></span>
                          <span className="wp-ledger-col-amount credit">-{formatCurrency(newPay, curr)}</span>
                          <span className="wp-ledger-col-balance">{formatCurrency(liveRemaining, curr)}</span>
                        </div>
                      )}
                    </div>

                    {/* Alt çizgi - Toplam */}
                    <div className="wp-ledger-footer">
                      <div className="wp-ledger-footer-row">
                        <span>Toplam Borç</span>
                        <div style={{ textAlign: 'right' }}>
                          <strong className="debit">{formatCurrency(totalAmt, curr)}</strong>
                          {toTRY(totalAmt, curr) !== null && <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 400 }}>₺{Number(toTRY(totalAmt, curr)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>}
                        </div>
                      </div>
                      <div className="wp-ledger-footer-row">
                        <span>Toplam Ödenen</span>
                        <div style={{ textAlign: 'right' }}>
                          <strong className="credit">{formatCurrency(totalPaid, curr)}</strong>
                          {totalPaid > 0 && toTRY(totalPaid, curr) !== null && <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 400 }}>₺{Number(toTRY(totalPaid, curr)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>}
                        </div>
                      </div>
                      <div className={`wp-ledger-footer-row result ${isComplete ? 'complete' : ''}`}>
                        <span>Kalan Borç</span>
                        <div style={{ textAlign: 'right' }}>
                          <strong>{formatCurrency(liveRemaining, curr)}</strong>
                          {liveRemaining > 0 && toTRY(liveRemaining, curr) !== null && <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 400 }}>₺{Number(toTRY(liveRemaining, curr)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>}
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="wp-ledger-progress">
                      <div className="wp-ledger-progress-bar">
                        <div className="wp-ledger-progress-fill" style={{ width: `${livePct}%` }} />
                      </div>
                      <span className="wp-ledger-progress-pct">%{livePct}</span>
                    </div>

                    {/* Yeni Ödeme Ekle */}
                    <div className="wp-ledger-input-section">
                      <label>Yeni Ödeme Ekle</label>
                      <div className="wp-partial-input">
                        <span>{currencyInfo[curr]?.symbol || '₺'}</span>
                        <PriceInput
                          value={partialNewAmount}
                          onChange={val => setPartialNewAmount(val)}
                          placeholder="0.00"
                          min="0"
                          max={remainingDebt}
                          autoFocus
                        />
                      </div>
                      {newPay > 0 && toTRY(newPay, curr) !== null && (
                        <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'right', marginTop: '4px' }}>
                          {formatCurrency(newPay, curr)} = <strong style={{ color: '#0f766e' }}>₺{Number(toTRY(newPay, curr)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </div>
                      )}
                      <div className="wp-quick-amounts">
                        <button onClick={() => setPartialNewAmount(Math.round(remainingDebt * 0.25))}>%25</button>
                        <button onClick={() => setPartialNewAmount(Math.round(remainingDebt * 0.5))}>%50</button>
                        <button onClick={() => setPartialNewAmount(Math.round(remainingDebt * 0.75))}>%75</button>
                        <button onClick={() => setPartialNewAmount(remainingDebt)}>Tamamı</button>
                      </div>
                    </div>

                    {isComplete && (
                      <div className="wp-ledger-complete">Ödeme tamamlanacak!</div>
                    )}
                  </div>
                );
              })()}

              <div className="wp-form-group">
                <label>Ödeme Yöntemi</label>
                <div className="wp-payment-methods">
                  {Object.entries(paymentMethodInfo).map(([key, info]) => (
                    <button
                      key={key}
                      className={`wp-method-btn ${partialPaymentMethod === key ? 'active' : ''}`}
                      onClick={() => setPartialPaymentMethod(key)}
                    >
                      <span className="wp-method-icon">{info.icon}</span>
                      <span className="wp-method-name">{info.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="wp-form-group">
                <label>Açıklama</label>
                <input type="text" value={partialNote} maxLength={255}
                  onChange={e => setPartialNote(e.target.value)}
                  placeholder="Örn: çek no, banka, teslim alan kişi..."
                />
              </div>
            </div>

            <div className="wp-modal-footer">
              <button className="wp-btn cancel" onClick={() => setShowPartialModal(false)}>Kapat</button>
              <button className="wp-btn save" onClick={() => {
                if (!partialNewAmount || Number(partialNewAmount) <= 0) {
                  setShowPartialModal(false);
                  setPartialPayment(null);
                  setPartialNewAmount('');
                  fetchPayments();
                  return;
                }
                handlePartialPayment();
              }}>
                <Coins size={16} /> {(!partialNewAmount || Number(partialNewAmount) <= 0) ? 'Kaydet ve Kapat' : 'Ödemeyi Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ödendi Onay Modal */}
      {showPaidModal && paidPayment && (
        <div className={`wp-modal-overlay ${closingModal === 'paid' ? 'closing' : ''}`}>
          <div className="wp-modal paid-confirm" onClick={e => e.stopPropagation()}>
            <div className="wp-modal-header">
              <h2>Ödeme Onayı</h2>
              <button className="wp-modal-close" onClick={() => handleCloseModal('paid', setShowPaidModal)}>×</button>
            </div>

            <div className="wp-modal-body">
              <div className="wp-partial-info">
                <h3>{paidPayment.title}</h3>
                <p>{paidPayment.recipient || categoryNames[paidPayment.category]}</p>
                <span className="wp-confirm-amount">{formatCurrency(paidPayment.amount, paidPayment.currency)}</span>
              </div>

              <div className="wp-form-group">
                <label>Ödeme Yöntemi Seçin</label>
                <div className="wp-payment-methods">
                  {Object.entries(paymentMethodInfo).map(([key, info]) => (
                    <button
                      key={key}
                      className={`wp-method-btn ${paidPaymentMethod === key ? 'active' : ''}`}
                      onClick={() => setPaidPaymentMethod(key)}
                    >
                      <span className="wp-method-icon">{info.icon}</span>
                      <span className="wp-method-name">{info.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="wp-modal-footer">
              <button className="wp-btn cancel" onClick={() => handleCloseModal('paid', setShowPaidModal)}>İptal</button>
              <button className="wp-btn save" onClick={handlePaidConfirm}>
                <Check size={16} /> Ödendi Olarak İşaretle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rapor Modal */}
      {showReportModal && (
        <div className={`wp-modal-overlay ${closingModal === 'report' ? 'closing' : ''}`}>
          <div className="wp-modal report-modal" onClick={e => e.stopPropagation()}>
            <div className="wp-modal-header report-header">
              <div className="report-header-title">
                <FileDown size={24} />
                <h2>Rapor Oluştur</h2>
              </div>
              <button className="wp-modal-close" onClick={() => handleCloseModal('report', setShowReportModal)}>×</button>
            </div>

            <div className="wp-modal-body report-body">
              {/* Tarih Aralığı */}
              <div className="report-section">
                <h3><Calendar size={18} /> Tarih Aralığı Seçin</h3>

                <div className="date-presets">
                  <button className="preset-btn" onClick={() => setDateRangePreset('thisWeek')}>Bu Hafta</button>
                  <button className="preset-btn" onClick={() => setDateRangePreset('thisMonth')}>Bu Ay</button>
                  <button className="preset-btn" onClick={() => setDateRangePreset('lastMonth')}>Geçen Ay</button>
                  <button className="preset-btn" onClick={() => setDateRangePreset('last3Months')}>Son 3 Ay</button>
                  <button className="preset-btn" onClick={() => setDateRangePreset('thisYear')}>Bu Yıl</button>
                </div>

                <div className="date-range-inputs">
                  <div className="date-input-group">
                    <label>Başlangıç Tarihi</label>
                    <input
                      type="date"
                      value={reportDateRange.startDate}
                      onChange={(e) => setReportDateRange({...reportDateRange, startDate: e.target.value})}
                    />
                  </div>
                  <span className="date-separator">→</span>
                  <div className="date-input-group">
                    <label>Bitiş Tarihi</label>
                    <input
                      type="date"
                      value={reportDateRange.endDate}
                      onChange={(e) => setReportDateRange({...reportDateRange, endDate: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Rapor Önizleme */}
              {reportPreview && (
                <div className="report-section preview-section">
                  <h3>📊 Rapor Önizleme</h3>

                  <div className="preview-stats">
                    <div className="preview-stat-card">
                      <span className="preview-stat-label">Toplam İşlem</span>
                      <span className="preview-stat-value">{reportPreview.totalPayments}</span>
                    </div>
                    <div className="preview-stat-card expense">
                      <span className="preview-stat-label">Toplam Borç</span>
                      <span className="preview-stat-value">₺{reportPreview.totalExpense.toLocaleString()}</span>
                    </div>
                    <div className="preview-stat-card income">
                      <span className="preview-stat-label">Toplam Alacak</span>
                      <span className="preview-stat-value">₺{reportPreview.totalIncome.toLocaleString()}</span>
                    </div>
                    <div className={`preview-stat-card net ${reportPreview.totalIncome - reportPreview.totalExpense >= 0 ? 'positive' : 'negative'}`}>
                      <span className="preview-stat-label">Net Bakiye</span>
                      <span className="preview-stat-value">
                        {reportPreview.totalIncome - reportPreview.totalExpense >= 0 ? '+' : ''}
                        ₺{(reportPreview.totalIncome - reportPreview.totalExpense).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>

                  <div className="preview-details">
                    <div className="preview-detail-row">
                      <span>✅ Ödenen:</span>
                      <strong>{reportPreview.paidCount} adet</strong>
                    </div>
                    <div className="preview-detail-row">
                      <span>⏳ Bekleyen:</span>
                      <strong>{reportPreview.pendingCount} adet</strong>
                    </div>
                    <div className="preview-detail-row">
                      <span>⚠️ Gecikmiş:</span>
                      <strong className="overdue">{reportPreview.overdueCount} adet</strong>
                    </div>
                  </div>

                  {/* Para Birimi Dağılımı */}
                  {Object.keys(reportPreview.byCurrency).length > 0 && (
                    <div className="preview-currency-section">
                      <h4>💰 Para Birimi Bazlı</h4>
                      <div className="preview-currency-grid">
                        {Object.entries(reportPreview.byCurrency).map(([curr, data]) => (
                          <div key={curr} className="preview-currency-card">
                            <span className="currency-icon">{currencyInfo[curr]?.icon || '💰'}</span>
                            <span className="currency-name">{currencyInfo[curr]?.name || curr}</span>
                            <div className="currency-values">
                              <span className="expense">-{formatCurrency(data.expense, curr)}</span>
                              <span className="income">+{formatCurrency(data.income, curr)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Format Seçimi */}
              <div className="report-section format-section">
                <h3><Download size={18} /> Export Formatı</h3>

                <div className="format-options">
                  <button
                    className={`format-option ${reportFormat === 'pdf' ? 'active' : ''}`}
                    onClick={() => setReportFormat('pdf')}
                  >
                    <div className="format-icon">📄</div>
                    <div className="format-info">
                      <span className="format-name">PDF / Yazdır</span>
                      <span className="format-desc">Profesyonel baskı için ideal</span>
                    </div>
                  </button>

                  <button
                    className={`format-option ${reportFormat === 'excel' ? 'active' : ''}`}
                    onClick={() => setReportFormat('excel')}
                  >
                    <div className="format-icon">📊</div>
                    <div className="format-info">
                      <span className="format-name">Excel / CSV</span>
                      <span className="format-desc">Veri analizi ve düzenleme için</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            <div className="wp-modal-footer report-footer">
              <button className="wp-btn cancel" onClick={() => setShowReportModal(false)}>
                <X size={16} /> İptal
              </button>
              <button
                className="wp-btn report-generate"
                onClick={reportFormat === 'pdf' ? generatePDF : generateExcel}
                disabled={reportLoading || !reportPreview || reportPreview.totalPayments === 0}
              >
                {reportLoading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Oluşturuluyor...
                  </>
                ) : (
                  <>
                    {reportFormat === 'pdf' ? <Printer size={16} /> : <Download size={16} />}
                    {reportFormat === 'pdf' ? 'PDF Oluştur / Yazdır' : 'Excel İndir'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Borç/alacak fotoğrafı büyük görüntüleyici (yakınlaştırılabilir) */}
      {wsPhotoViewer && wsPhotoViewer.photos?.length > 0 && (() => {
        const { photos, index } = wsPhotoViewer;
        const go = (d) => setWsPhotoViewer({ photos, index: (index + d + photos.length) % photos.length });
        const navBtn = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: '46px', height: '46px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 };
        return (
          <div onClick={() => setWsPhotoViewer(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <button type="button" onClick={() => setWsPhotoViewer(null)} title="Kapat"
              style={{ position: 'absolute', top: '16px', right: '16px', width: '46px', height: '46px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
              <X size={24} />
            </button>
            {photos.length > 1 && (
              <div style={{ position: 'absolute', top: '24px', left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: '14px', zIndex: 2 }}>{index + 1} / {photos.length}</div>
            )}
            <div onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
              <ZoomablePhoto src={`${API_HOST}${photos[index]}`} alt={`Fotoğraf ${index + 1}`} onBackgroundClick={() => setWsPhotoViewer(null)} />
              {photos.length > 1 && (
                <>
                  <button type="button" onClick={() => go(-1)} title="Önceki" style={{ ...navBtn, left: '8px' }}><ChevronLeft size={26} /></button>
                  <button type="button" onClick={() => go(1)} title="Sonraki" style={{ ...navBtn, right: '8px' }}><ChevronRight size={26} /></button>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Arşiv Modal */}
      {showArchiveModal && (() => {
        // Tüm toptancıların birleşik borç geçmişi (arşivlenmiş + aktif tümü)
        const allEntries = [];
        payments.forEach(p => {
          allEntries.push({
            id: `d_${p.id}`, type: 'debit', date: new Date(p.created_at || p.due_date),
            wholesaler: p.wholesaler_name || p.wholesaler_company || p.recipient || '—',
            title: p.title, amount: Number(p.amount), currency: p.currency || 'TRY',
            invoice_issued: p.invoice_issued, status: p.status, archived: p.archived
          });
        });

        // Arama filtresi
        const filtered = archiveSearch
          ? allEntries.filter(e => {
              const s = searchFold(archiveSearch);
              return searchFold(e.wholesaler).includes(s) || searchFold(e.title).includes(s);
            })
          : allEntries;

        filtered.sort((a, b) => b.date - a.date);

        // Birim bazlı toplamlar
        const totals = {};
        allEntries.filter(e => e.type === 'debit' && !e.archived).forEach(e => {
          const c = e.currency;
          if (!totals[c]) totals[c] = { total: 0, invoiced: 0 };
          totals[c].total += e.amount;
          if (e.invoice_issued) totals[c].invoiced += e.amount;
        });
        const thS = { padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #cbd5e1', whiteSpace: 'nowrap', background: '#f1f5f9', position: 'sticky', top: 0, zIndex: 1 };
        const tdS = { padding: '8px 10px', borderBottom: '1px solid #e2e8f0', fontSize: '11px', whiteSpace: 'nowrap' };

        return (
          <div className="wp-modal-overlay">
            <div className="wp-archive-sheet" style={{
              background: '#fff', borderRadius: '16px', width: '95%', maxWidth: '900px',
              maxHeight: '92vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
            }}>
              {/* Header */}
              <div className="wp-archive-sheet-header" style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                padding: '16px 24px', color: 'white', display: 'flex', alignItems: 'center', gap: '12px'
              }}>
                <ScrollText size={22} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '17px', fontWeight: 700 }}>Tüm Borç Kayıtları</div>
                  <div style={{ fontSize: '12px', opacity: 0.7 }}>{filtered.length} kayıt</div>
                </div>
                {/* Arama */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '6px 12px', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <Search size={14} style={{ opacity: 0.6 }} />
                  <input type="text" placeholder="Ara..." value={archiveSearch} onChange={e => setArchiveSearch(e.target.value)}
                    style={{ border: 'none', outline: 'none', background: 'transparent', color: 'white', fontSize: '12px', width: '120px' }} />
                  {archiveSearch && <button onClick={() => setArchiveSearch('')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '14px' }}>×</button>}
                </div>
                <button onClick={() => setShowArchiveModal(false)} style={{
                  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white', borderRadius: '8px', width: '32px', height: '32px',
                  cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>×</button>
              </div>

              {/* Tablo */}
              <div style={{ flex: 1, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thS, paddingLeft: '16px', width: '35px' }}>#</th>
                      <th style={{ ...thS, width: '75px' }}>Tarih</th>
                      <th style={thS}>Toptancı</th>
                      <th style={thS}>Açıklama</th>
                      <th style={{ ...thS, width: '55px', textAlign: 'center' }}>Birim</th>
                      <th style={{ ...thS, width: '100px', textAlign: 'right' }}>Tutar</th>
                      <th style={{ ...thS, width: '60px', textAlign: 'center' }}>Durum</th>
                      <th style={{ ...thS, width: '55px', textAlign: 'center', paddingRight: '16px' }}>Fatura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((e, idx) => (
                      <tr key={e.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}
                        onMouseEnter={ev => { ev.currentTarget.style.background = '#f0fdfa'; }}
                        onMouseLeave={ev => { ev.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f8fafc'; }}
                      >
                        <td style={{ ...tdS, paddingLeft: '16px', color: '#94a3b8', fontSize: '10px' }}>{idx + 1}</td>
                        <td style={{ ...tdS, color: '#64748b' }}>{e.date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</td>
                        <td style={{ ...tdS, fontWeight: 600, color: '#1e293b' }}>{e.wholesaler}</td>
                        <td style={{ ...tdS, color: '#475569', whiteSpace: 'normal', maxWidth: '200px' }}>{e.title}</td>
                        <td style={{ ...tdS, textAlign: 'center' }}>
                          <span style={{ fontSize: '10px', padding: '2px 6px', background: '#f1f5f9', borderRadius: '4px', fontWeight: 600, color: '#64748b' }}>{e.currency}</span>
                        </td>
                        <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', fontSize: '12px', color: '#dc2626' }}>
                          {formatCurrency(e.amount, e.currency)}
                        </td>
                        <td style={{ ...tdS, textAlign: 'center' }}>
                          <span style={{
                            fontSize: '9px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600,
                            background: e.status === 'paid' ? '#dcfce7' : e.status === 'partial' ? '#e0f2fe' : '#fef9c3',
                            color: e.status === 'paid' ? '#16a34a' : e.status === 'partial' ? '#0284c7' : '#ca8a04'
                          }}>
                            {e.status === 'paid' ? 'Ödendi' : e.status === 'partial' ? 'Kısmi' : 'Bekliyor'}
                          </span>
                        </td>
                        <td style={{ ...tdS, textAlign: 'center', paddingRight: '16px' }}>
                          <span
                            onClick={async (ev) => {
                              ev.stopPropagation();
                              // e.id formatı: 'd_<paymentId>'
                              const paymentId = String(e.id).startsWith('d_') ? String(e.id).slice(2) : e.id;
                              const newVal = !e.invoice_issued;
                              try {
                                const res = await fetch(`${API_URL}/wholesaler-payments/${paymentId}/invoice`, {
                                  method: 'PATCH',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ invoice_issued: newVal })
                                });
                                if (!res.ok) throw new Error('Güncellenemedi');
                                // Local state'i güncelle ki modal hemen yansıtsın
                                setPayments(prev => prev.map(p => p.id === Number(paymentId) ? { ...p, invoice_issued: newVal ? 1 : 0 } : p));
                                toast.success(newVal ? 'Fatura kesildi olarak işaretlendi' : 'Fatura kesilmedi olarak işaretlendi');
                              } catch (err) {
                                toast.error('Fatura durumu güncellenemedi');
                              }
                            }}
                            title="Tıklayarak fatura durumunu değiştir"
                            style={{
                              fontSize: '9px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600,
                              background: e.invoice_issued ? '#dcfce7' : '#fef2f2',
                              color: e.invoice_issued ? '#16a34a' : '#dc2626',
                              cursor: 'pointer',
                              userSelect: 'none',
                              transition: 'all 0.15s',
                              display: 'inline-block'
                            }}
                            onMouseEnter={ev => { ev.currentTarget.style.transform = 'scale(1.1)'; ev.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.1)'; }}
                            onMouseLeave={ev => { ev.currentTarget.style.transform = 'scale(1)'; ev.currentTarget.style.boxShadow = 'none'; }}
                          >
                            {e.invoice_issued ? 'Evet' : 'Hayır'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                    {archiveSearch ? 'Arama sonucu bulunamadı' : 'Henüz borç kaydı yok'}
                  </div>
                )}
              </div>

              {/* Dip Toplamlar */}
              {Object.keys(totals).length > 0 && (
                <div style={{ borderTop: '2px solid #cbd5e1', background: '#f1f5f9', flexShrink: 0, padding: '10px 16px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569' }}>TOPLAM:</span>
                  {Object.entries(totals).map(([curr, t]) => (
                    <div key={curr} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <span style={{ padding: '2px 6px', background: '#e2e8f0', borderRadius: '4px', fontWeight: 700, color: '#475569', fontSize: '10px' }}>{curr}</span>
                      <span style={{ fontWeight: 700, color: '#dc2626', fontFamily: 'monospace' }}>{formatCurrency(t.total, curr)}</span>
                      <span style={{ fontSize: '10px', color: '#94a3b8' }}>({Math.round((t.invoiced / Math.max(t.total, 1)) * 100)}% faturalı)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Özet Detay Modal */}
      {showSummaryDetailModal && (() => {
        const activePayments = payments.filter(p => !p.archived);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let title, filterFn, color;
        if (summaryDetailType === 'debt') {
          title = 'Toplam Borç Detayı';
          color = '#ef4444';
          filterFn = p => {
            if (p.payment_type === 'income') return false;
            const isPaid = p.status === 'paid';
            const remaining = isPaid ? 0 : Math.max(0, (Number(p.amount) || 0) - (Number(p.paid_amount) || 0));
            return remaining > 0;
          };
        } else if (summaryDetailType === 'paid') {
          title = 'Toplam Ödenen Detayı';
          color = '#22c55e';
          filterFn = p => {
            if (p.payment_type === 'income') return false;
            const isPaid = p.status === 'paid';
            const paidAmt = isPaid ? (Number(p.amount) || 0) : (Number(p.paid_amount) || 0);
            return paidAmt > 0;
          };
        } else if (summaryDetailType === 'credit') {
          title = 'Toplam Alacak Detayı';
          color = '#22c55e';
          filterFn = p => {
            if (p.payment_type !== 'income') return false;
            const isPaid = p.status === 'paid';
            const remaining = isPaid ? 0 : Math.max(0, (Number(p.amount) || 0) - (Number(p.paid_amount) || 0));
            return remaining > 0;
          };
        } else {
          title = 'Gecikmiş Ödemeler';
          color = '#f59e0b';
          filterFn = p => {
            if (p.payment_type === 'income') return false;
            const isPaid = p.status === 'paid';
            if (isPaid) return false;
            return p.due_date && new Date(p.due_date) < today;
          };
        }

        const filtered = activePayments.filter(filterFn);

        // Para birimine göre grupla
        const byCurr = {};
        filtered.forEach(p => {
          const curr = p.currency || 'TRY';
          if (!byCurr[curr]) byCurr[curr] = { items: [], total: 0, paid: 0, remaining: 0 };
          const total = Number(p.amount) || 0;
          const isPaid = p.status === 'paid';
          const paidAmt = isPaid ? total : (Number(p.paid_amount) || 0);
          const remaining = isPaid ? 0 : Math.max(0, total - paidAmt);
          byCurr[curr].items.push(p);
          byCurr[curr].total += total;
          byCurr[curr].paid += paidAmt;
          byCurr[curr].remaining += remaining;
        });

        const getWholesalerName = (p) => {
          const w = wholesalers.find(x => x.id === p.wholesaler_id);
          return w ? w.name : (p.recipient || 'Bilinmiyor');
        };

        return (
          <div className="wp-modal-overlay">
            <div style={{ background: '#fff', borderRadius: '16px', width: '95%', maxWidth: '800px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }}></span>
                  {title}
                  <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#94a3b8' }}>({filtered.length} kayıt)</span>
                </h3>
                <button onClick={() => setShowSummaryDetailModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#94a3b8', padding: '4px' }}>×</button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
                {Object.entries(byCurr).map(([curr, data]) => (
                  <div key={curr} style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', padding: '8px 12px', background: '#f8fafc', borderRadius: '8px' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
                        {currencyInfo[curr]?.icon} {currencyInfo[curr]?.name || curr}
                      </span>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.9rem', color }}>
                          {summaryDetailType === 'paid' ? formatCurrency(data.paid, curr) : formatCurrency(data.remaining, curr)}
                        </span>
                        {curr !== 'TRY' && liveRates[curr] > 0 && (
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                            ≈ ₺{((summaryDetailType === 'paid' ? data.paid : data.remaining) * liveRates[curr]).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        )}
                      </div>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Toptancı</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Açıklama</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Vade</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Toplam</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Ödenen</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Kalan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.items.map(p => {
                          const total = Number(p.amount) || 0;
                          const isPaid = p.status === 'paid';
                          const paidAmt = isPaid ? total : (Number(p.paid_amount) || 0);
                          const remaining = isPaid ? 0 : Math.max(0, total - paidAmt);
                          const isOverdue = !isPaid && p.due_date && new Date(p.due_date) < today;
                          return (
                            <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9', background: isOverdue ? '#fffbeb' : 'transparent' }}>
                              <td style={{ padding: '6px 8px', fontWeight: 500, color: '#1e293b' }}>{getWholesalerName(p)}</td>
                              <td style={{ padding: '6px 8px', color: '#64748b' }}>{p.title || '-'}</td>
                              <td style={{ padding: '6px 8px', color: isOverdue ? '#f59e0b' : '#64748b', fontWeight: isOverdue ? 600 : 400 }}>
                                {p.due_date ? new Date(p.due_date).toLocaleDateString('tr-TR') : '-'}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#1e293b' }}>{formatCurrency(total, curr)}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#22c55e' }}>{formatCurrency(paidAmt, curr)}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: remaining > 0 ? '#ef4444' : '#22c55e' }}>{formatCurrency(remaining, curr)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
                {filtered.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '0.85rem' }}>Kayıt bulunamadı</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Log Modal */}
      <LogModal
        isOpen={showLogModal}
        onClose={() => setShowLogModal(false)}
        module="wholesaler-payments"
        title="Toptancı Ödemeleri"
        showEntityCode={false}
      />

      {/* Toptancı Detay Modal */}
      {showWDetail && (() => {
        const group = selectedWId
          ? wholesalerGroups.find(g => g.wholesaler_id === selectedWId)
          : wholesalerGroups.find(g => g.key === 'other');
        if (!group) return null;
        const w = group.wholesaler;
        const initials = group.name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();

        // Ledger entries: borçlar + ödemeler birleşik
        const entries = [];
        group.payments.forEach(p => {
          entries.push({
            id: `d_${p.id}`,
            type: p.payment_type === 'income' ? 'credit_balance' : 'debit',
            date: new Date(p.created_at || p.due_date),
            title: p.title,
            amount: Number(p.amount),
            currency: p.currency || 'TRY',
            invoice_issued: p.invoice_issued,
            payment_id: p.id,
            status: p.status,
            payment_type: p.payment_type || 'expense',
            photos: parseWsPhotos(p.photos)
          });
        });
        // Tek seferde girilip birden çok borca (FIFO) dağıtılan ödeme parçaları tek satırda gösterilir:
        // aynı birim + ödeme türü + açıklama + fatura durumu, farklı borç, en fazla 10 sn arayla
        const creditEntries = wHistories.map(h => ({
          id: `c_${h.id}`, type: 'credit', date: new Date(h.created_at),
          title: `Ödeme (${paymentMethodInfo[h.payment_method]?.name || 'Nakit'})${h.note ? ` — ${h.note}` : ''}`,
          amount: Number(h.amount), currency: h.currency || 'TRY',
          invoice_issued: h.invoice_issued, history_id: h.id, payment_id: h.payment_id,
          payment_method: h.payment_method || 'cash', note: h.note || '',
          debtTitle: group.payments.find(p => p.id === h.payment_id)?.title || ''
        })).sort((a, b) => a.date - b.date || a.history_id - b.history_id);
        const payGroups = [];
        creditEntries.forEach(c => {
          const g = payGroups.find(pg =>
            pg.currency === c.currency && pg.payment_method === c.payment_method && pg.note === c.note &&
            !!pg.invoice_issued === !!c.invoice_issued && Math.abs(c.date - pg.parts[pg.parts.length - 1].date) <= 10000 &&
            !pg.parts.some(pt => pt.payment_id === c.payment_id));
          if (g) g.parts.push(c);
          else payGroups.push({ ...c, parts: [c] });
        });
        payGroups.forEach(g => {
          if (g.parts.length === 1) { entries.push(g.parts[0]); return; }
          entries.push({
            ...g, id: `cg_${g.parts[0].history_id}`, history_id: undefined, payment_id: undefined,
            amount: Math.round(g.parts.reduce((s, pt) => s + pt.amount, 0) * 100) / 100
          });
        });
        entries.sort((a, b) => b.date - a.date);

        const firstUnpaid = group.payments.find(p => p.status === 'pending' || p.status === 'partial');

        return (
          <div className="wp-modal-overlay wp-wd-overlay">
            <div className="wp-wd" style={{
              background: '#fff', borderRadius: '16px', width: '97%',
              maxWidth: '1320px',
              maxHeight: '92vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
              transition: 'max-width 0.2s ease'
            }}>
              {/* Header - Profil */}
              <div className="wp-wd-header" style={{
                background: 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)',
                padding: '16px 24px', color: 'white', flexShrink: 0
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{
                    width: '50px', height: '50px', borderRadius: '50%',
                    background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '18px', fontWeight: 700, flexShrink: 0
                  }}>
                    {initials}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '18px', fontWeight: 700 }}>{group.name}</div>
                    <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '2px' }}>
                      {[w?.company_name, w?.phone, w?.email, w?.address].filter(Boolean).join(' · ') || 'Toptancı'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button onClick={() => setShowWDetail(false)} style={{
                      background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                      color: 'white', borderRadius: '8px', width: '32px', height: '32px',
                      cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>×</button>
                  </div>
                </div>

              </div>

              {/* Özet Tablosu - Borç Ekle ve Borç Öde tab'larında */}
              {wDetailTab !== 'history' && <div className="wp-wd-summary" style={{ padding: '0', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                {Object.keys(group.totalByCurrency).length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Birim</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '10px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Toplam</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '10px', fontWeight: 600, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ödenen</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '10px', fontWeight: 600, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Kalan</th>
                        <th style={{ padding: '8px 16px', textAlign: 'right', fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', width: '80px' }}>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(group.totalByCurrency).map(([curr, data]) => {
                        const pct = data.total > 0 ? Math.min(100, Math.round((data.paid / data.total) * 100)) : 0;
                        return (
                          <tr key={curr} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 16px' }}>
                              <span style={{ fontSize: '11px', padding: '2px 8px', background: '#e2e8f0', borderRadius: '4px', fontWeight: 700, color: '#475569' }}>{currencyInfo[curr]?.icon} {curr}</span>
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e293b', fontFamily: 'monospace', fontSize: '13px' }}>{formatCurrency(data.total, curr)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#22c55e', fontFamily: 'monospace', fontSize: '13px' }}>{formatCurrency(data.paid, curr)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: data.remaining > 0 ? '#dc2626' : '#22c55e', fontFamily: 'monospace', fontSize: '13px' }}>{formatCurrency(data.remaining, curr)}</td>
                            <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                              <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden', width: '60px', marginLeft: 'auto' }}>
                                <div style={{ height: '100%', background: pct >= 100 ? '#22c55e' : '#0f766e', borderRadius: '2px', width: `${pct}%` }} />
                              </div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>%{pct}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Henüz borç kaydı yok</div>
                )}
              </div>}

              {/* Tab Butonları */}
              <div className="wp-wd-tabs" style={{ display: 'flex', padding: '4px', background: '#f1f5f9', flexShrink: 0, gap: '4px', borderBottom: '1px solid #e2e8f0' }}>
                {[
                  ...(selectedWId ? [{ id: 'addDebt', label: 'Borç Ekle', icon: <Plus size={15} /> }] : []),
                  { id: 'payDebt', label: 'Borç Öde', icon: <Coins size={15} /> },
                  ...(selectedWId ? [{ id: 'addCredit', label: 'Alacak Ekle', icon: <Plus size={15} /> }] : []),
                  { id: 'history', label: 'Geçmiş', icon: <ScrollText size={15} /> }
                ].map(tab => (
                  <button key={tab.id} onClick={() => setWDetailTab(tab.id)}
                    style={{
                      flex: 1, padding: '10px 16px', border: 'none', cursor: 'pointer',
                      borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      color: wDetailTab === tab.id ? '#fff' : '#64748b',
                      background: wDetailTab === tab.id ? '#0f766e' : 'transparent',
                      boxShadow: wDetailTab === tab.id ? '0 2px 8px rgba(15,118,110,0.3)' : 'none',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => { if (wDetailTab !== tab.id) { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#1e293b'; } }}
                    onMouseLeave={e => { if (wDetailTab !== tab.id) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; } }}
                  >{tab.icon} {tab.label}</button>
                ))}
              </div>

              {/* Tab içeriği scroll alanı (addDebt / payDebt) */}
              {wDetailTab !== 'history' && <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {/* Tab: Borç Ekle / Alacak Ekle */}
              {(wDetailTab === 'addDebt' || wDetailTab === 'addCredit') && (() => {
                const debtNum = Number(wNewDebt.amount) || 0;
                const isCredit = wDetailTab === 'addCredit';
                const primaryColor = isCredit ? '#16a34a' : '#dc2626';
                const primaryBg = isCredit ? '#f0fdf4' : '#fef2f2';
                const primaryBorder = isCredit ? '#bbf7d0' : '#fecaca';
                const ctaColor = isCredit ? '#15803d' : '#0f766e';
                const ctaHover = isCredit ? '#166534' : '#115e59';
                const existingForCurr = group.totalByCurrency[wNewDebt.currency];
                const currentNet = existingForCurr ? (existingForCurr.net || 0) : 0;
                const afterAddNet = currentNet + (isCredit ? -debtNum : debtNum);
                const lbl = { fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' };
                const inp = { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };

                return (
                  <div className="wp-wd-form" style={{ padding: '16px 24px', maxWidth: '720px', margin: '0 auto' }}>
                    <div className="wp-wd-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={lbl}>Açıklama *</label>
                        <input type="text" placeholder={isCredit ? 'Alacak açıklaması...' : 'Borç açıklaması...'} value={wNewDebt.title}
                          onChange={e => setWNewDebt(p => ({ ...p, title: e.target.value }))} style={inp} />
                      </div>
                      <div>
                        <label style={lbl}>Birim</label>
                        <select value={wNewDebt.currency} onChange={e => setWNewDebt(p => ({ ...p, currency: e.target.value }))}
                          style={{ ...inp, background: '#fff' }}>
                          {Object.entries(currencyInfo).map(([code, info]) => (
                            <option key={code} value={code}>{info.icon} {info.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="wp-wd-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                      <div>
                        <label style={lbl}>Tutar ({currencyInfo[wNewDebt.currency]?.symbol}) *</label>
                        <PriceInput placeholder="0.00" value={wNewDebt.amount}
                          onChange={val => setWNewDebt(p => ({ ...p, amount: val }))}
                          style={{ ...inp, fontFamily: 'monospace', fontSize: '15px' }} />
                        {debtNum > 0 && toTRY(debtNum, wNewDebt.currency) !== null && (
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', textAlign: 'right' }}>
                            {formatCurrency(debtNum, wNewDebt.currency)} = <strong style={{ color: '#0f766e' }}>₺{Number(toTRY(debtNum, wNewDebt.currency)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={lbl}>Vade Tarihi</label>
                        <input type="date" value={wNewDebt.due_date}
                          onChange={e => setWNewDebt(p => ({ ...p, due_date: e.target.value }))} style={inp} />
                      </div>
                    </div>
                    <div style={{ marginBottom: '14px' }}>
                      <label style={lbl}>Fotoğraflar</label>
                      {renderWsPhotoPicker(wNewDebt.photos, photos => setWNewDebt(p => ({ ...p, photos })), 'wp-detail-photo')}
                    </div>
                    {/* Anlık hesaplama */}
                    <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>Mevcut Bakiye</span>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: currentNet > 0 ? '#dc2626' : currentNet < 0 ? '#16a34a' : '#94a3b8', fontFamily: 'monospace' }}>
                          {Math.abs(currentNet) > 0.0001 ? `${formatCurrency(Math.abs(currentNet), wNewDebt.currency)}${currentNet < 0 ? ' (alacak)' : ''}` : '—'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>Eklenecek</span>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: debtNum > 0 ? primaryColor : '#94a3b8', fontFamily: 'monospace' }}>
                          {debtNum > 0 ? `${isCredit ? '-' : '+'}${formatCurrency(debtNum, wNewDebt.currency)}` : '—'}
                        </span>
                      </div>
                      <div style={{ height: '1px', background: '#e2e8f0', margin: '6px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>Yeni Bakiye</span>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '16px', fontWeight: 800, fontFamily: 'monospace', color: afterAddNet > 0 ? '#dc2626' : afterAddNet < 0 ? '#16a34a' : '#94a3b8' }}>
                            {Math.abs(afterAddNet) > 0.0001 ? `${formatCurrency(Math.abs(afterAddNet), wNewDebt.currency)}${afterAddNet < 0 ? ' alacak' : ''}` : '—'}
                          </span>
                          {Math.abs(afterAddNet) > 0.0001 && toTRY(Math.abs(afterAddNet), wNewDebt.currency) !== null && (
                            <div style={{ fontSize: '10px', color: '#94a3b8' }}>₺{Number(toTRY(Math.abs(afterAddNet), wNewDebt.currency)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <button onClick={handleWDetailAddDebt}
                      disabled={!wNewDebt.title || debtNum <= 0}
                      style={{
                        width: '100%', padding: '11px', border: 'none', borderRadius: '8px',
                        fontSize: '14px', fontWeight: 700, cursor: wNewDebt.title && debtNum > 0 ? 'pointer' : 'not-allowed',
                        background: wNewDebt.title && debtNum > 0 ? ctaColor : '#d1d5db', color: 'white',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => { if (wNewDebt.title && debtNum > 0) e.currentTarget.style.background = ctaHover; }}
                      onMouseLeave={e => { if (wNewDebt.title && debtNum > 0) e.currentTarget.style.background = ctaColor; }}
                    >
                      {debtNum > 0
                        ? `${formatCurrency(debtNum, wNewDebt.currency)} ${isCredit ? 'Alacak' : 'Borç'} Ekle`
                        : `${isCredit ? 'Alacak' : 'Borç'} Ekle`}
                    </button>
                  </div>
                );
              })()}

              {/* Tab: Borç Öde */}
              {wDetailTab === 'payDebt' && (() => {
                const totalRemaining = {};
                const totalCredit = {};
                group.payments
                  .filter(p => p.status === 'pending' || p.status === 'partial')
                  .forEach(p => {
                    const curr = p.currency || 'TRY';
                    const open = Number(p.amount) - (Number(p.paid_amount) || 0);
                    if (p.payment_type === 'income') {
                      if (!totalCredit[curr]) totalCredit[curr] = 0;
                      totalCredit[curr] += open;
                    } else {
                      if (!totalRemaining[curr]) totalRemaining[curr] = 0;
                      totalRemaining[curr] += open;
                    }
                  });
                const remainingEntries = Object.entries(totalRemaining);
                const activeCurr = wPayCurrency && totalRemaining[wPayCurrency] ? wPayCurrency : (remainingEntries.length > 0 ? remainingEntries[0][0] : '');
                const activeRemaining = totalRemaining[activeCurr] || 0;
                const activeCredit = totalCredit[activeCurr] || 0;
                const activeNet = activeRemaining - activeCredit;
                const payNum = Number(wPayAmount) || 0;
                const afterPay = Math.max(0, activeRemaining - payNum);
                const lbl = { fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' };

                return (
                  <div className="wp-wd-form" style={{ padding: '16px 24px', maxWidth: '720px', margin: '0 auto' }}>
                    {remainingEntries.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Check size={48} color="#22c55e" style={{ opacity: 0.5, marginBottom: '8px' }} />
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#22c55e' }}>Tüm borçlar ödenmiş!</div>
                      </div>
                    ) : (
                      <>
                        {/* Birim seçimi */}
                        <div style={{ marginBottom: '12px' }}>
                          <label style={lbl}>Borç Birimi</label>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {remainingEntries.map(([curr, amt]) => {
                              const cr = totalCredit[curr] || 0;
                              return (
                                <button key={curr}
                                  onClick={() => { setWPayCurrency(curr); setWPayAmount(''); }}
                                  style={{
                                    padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s',
                                    border: activeCurr === curr ? '2px solid #0f766e' : '1px solid #e2e8f0',
                                    background: activeCurr === curr ? '#f0fdfa' : '#fff',
                                    fontSize: '13px', fontWeight: 700, color: '#dc2626', fontFamily: 'monospace',
                                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.3
                                  }}
                                >
                                  <span>{formatCurrency(amt, curr)}</span>
                                  {cr > 0 && <span style={{ fontSize: '10px', fontWeight: 600, color: '#16a34a', marginTop: '2px' }}>-{formatCurrency(cr, curr)} alacak</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {/* Ödeme tutarı */}
                        <div style={{ marginBottom: '12px' }}>
                          <label style={lbl}>Ödeme Tutarı ({currencyInfo[activeCurr]?.symbol || activeCurr})</label>
                          <PriceInput placeholder="0.00" value={wPayAmount}
                            onChange={val => setWPayAmount(val)}
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace' }}
                          />
                          {payNum > 0 && toTRY(payNum, activeCurr) !== null && (
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', textAlign: 'right' }}>
                              {formatCurrency(payNum, activeCurr)} = <strong style={{ color: '#0f766e' }}>₺{Number(toTRY(payNum, activeCurr)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </div>
                          )}
                        </div>

                        {/* Hızlı tutarlar */}
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
                          {[0.25, 0.5, 0.75, 1].map(pct => (
                            <button key={pct} onClick={() => setWPayAmount(String(Math.round(activeRemaining * pct * 100) / 100))}
                              style={{
                                flex: 1, padding: '6px', border: '1px solid #e2e8f0', borderRadius: '6px',
                                background: '#f8fafc', fontSize: '11px', fontWeight: 600, color: '#475569',
                                cursor: 'pointer', transition: 'all 0.15s'
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; }}
                            >%{pct * 100}</button>
                          ))}
                        </div>

                        {/* Ödeme türü */}
                        <div style={{ marginBottom: '12px' }}>
                          <label style={lbl}>Ödeme Türü</label>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {Object.entries(paymentMethodInfo).map(([key, info]) => (
                              <button key={key} type="button" onClick={() => setWPayMethod(key)}
                                style={{
                                  padding: '7px 12px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s',
                                  border: wPayMethod === key ? '2px solid #0f766e' : '1px solid #e2e8f0',
                                  background: wPayMethod === key ? '#f0fdfa' : '#fff',
                                  fontSize: '13px', fontWeight: wPayMethod === key ? 700 : 500,
                                  color: wPayMethod === key ? '#0f766e' : '#475569'
                                }}
                              >{info.icon} {info.name}</button>
                            ))}
                          </div>
                        </div>

                        {/* Açıklama */}
                        <div style={{ marginBottom: '12px' }}>
                          <label style={lbl}>Açıklama</label>
                          <input type="text" value={wPayNote} maxLength={255}
                            onChange={e => setWPayNote(e.target.value)}
                            placeholder="Örn: çek no, banka, teslim alan kişi..."
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>

                        {/* Anlık hesaplama */}
                        <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>Kalan Borç</span>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#dc2626', fontFamily: 'monospace' }}>{formatCurrency(activeRemaining, activeCurr)}</span>
                          </div>
                          {activeCredit > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                              <span style={{ fontSize: '12px', color: '#64748b' }}>Alacak</span>
                              <span style={{ fontSize: '13px', fontWeight: 700, color: '#16a34a', fontFamily: 'monospace' }}>-{formatCurrency(activeCredit, activeCurr)}</span>
                            </div>
                          )}
                          {activeCredit > 0 && (
                            <>
                              <div style={{ height: '1px', background: '#e2e8f0', margin: '6px 0' }} />
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>Net {activeNet > 0 ? 'Borç' : activeNet < 0 ? 'Alacak' : ''}</span>
                                <span style={{ fontSize: '13px', fontWeight: 800, color: activeNet > 0 ? '#dc2626' : activeNet < 0 ? '#16a34a' : '#94a3b8', fontFamily: 'monospace' }}>
                                  {Math.abs(activeNet) < 0.0001 ? '—' : formatCurrency(Math.abs(activeNet), activeCurr)}
                                </span>
                              </div>
                            </>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>Ödenecek</span>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: payNum > 0 ? '#22c55e' : '#94a3b8', fontFamily: 'monospace' }}>
                              {payNum > 0 ? `-${formatCurrency(payNum, activeCurr)}` : '—'}
                            </span>
                          </div>
                          <div style={{ height: '1px', background: '#e2e8f0', margin: '6px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>Ödeme Sonrası Kalan</span>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontSize: '16px', fontWeight: 800, fontFamily: 'monospace', color: afterPay > 0 ? '#dc2626' : '#22c55e' }}>
                                {formatCurrency(afterPay, activeCurr)}
                              </span>
                              {afterPay > 0 && toTRY(afterPay, activeCurr) !== null && (
                                <div style={{ fontSize: '10px', color: '#94a3b8' }}>₺{Number(toTRY(afterPay, activeCurr)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                              )}
                            </div>
                          </div>
                          {payNum > 0 && afterPay === 0 && payNum === activeRemaining && (
                            <div style={{ marginTop: '6px', padding: '5px 10px', background: '#dcfce7', borderRadius: '6px', textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#16a34a' }}>
                              Bu birim borcu tamamen kapanacak!
                            </div>
                          )}
                          {payNum > activeRemaining && (
                            <div style={{ marginTop: '6px', padding: '5px 10px', background: '#fef9c3', borderRadius: '6px', textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#ca8a04' }}>
                              Borç aşılıyor; fazla {formatCurrency(payNum - activeRemaining, activeCurr)} alacak olarak eklenecek
                            </div>
                          )}
                        </div>

                        <button onClick={() => { const curr = wPayCurrency || activeCurr; if (!wPayCurrency) setWPayCurrency(activeCurr); handleWDetailPayment(curr); }}
                          disabled={payNum <= 0}
                          style={{
                            width: '100%', padding: '11px', border: 'none', borderRadius: '8px',
                            fontSize: '14px', fontWeight: 700, cursor: payNum > 0 ? 'pointer' : 'not-allowed',
                            background: payNum > 0 ? '#22c55e' : '#d1d5db', color: 'white',
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={e => { if (payNum > 0) e.currentTarget.style.background = '#16a34a'; }}
                          onMouseLeave={e => { if (payNum > 0) e.currentTarget.style.background = '#22c55e'; }}
                        >
                          {payNum > 0 ? `${formatCurrency(Math.min(payNum, activeRemaining), activeCurr)} Öde` : 'Ödemeyi Kaydet'}
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}
              </div>}

              {/* Tab: Geçmiş - Ledger Tablosu */}
              {wDetailTab === 'history' && <><div className="wp-wd-history" style={{ flex: 1, overflow: 'auto', padding: '0' }}>
                {entries.length === 0 ? (
                  <div style={{ padding: '40px 24px', textAlign: 'center', color: '#94a3b8' }}>
                    <DollarSign size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
                    <div>Henüz işlem yok</div>
                  </div>
                ) : (() => {
                  // Bakiye hesapla (birim bazlı)
                  // entries yeniden eskiye sıralı; yürüyen bakiye eskiden yeniye birikmeli
                  const runningBalances = {};
                  const entriesWithBalance = [...entries].reverse().map(e => {
                    const curr = e.currency;
                    if (!runningBalances[curr]) runningBalances[curr] = 0;
                    if (e.type === 'debit') runningBalances[curr] += e.amount;
                    else runningBalances[curr] -= e.amount;
                    return { ...e, balance: runningBalances[curr] };
                  }).reverse();

                  // Toplamlar (birim bazlı)
                  const totals = {};
                  entriesWithBalance.forEach(e => {
                    const curr = e.currency;
                    if (!totals[curr]) totals[curr] = { debit: 0, credit: 0, invoicedDebit: 0, invoicedCredit: 0 };
                    if (e.type === 'debit') {
                      totals[curr].debit += e.amount;
                      if (e.invoice_issued) totals[curr].invoicedDebit += e.amount;
                    } else {
                      totals[curr].credit += e.amount;
                      if (e.invoice_issued) totals[curr].invoicedCredit += e.amount;
                    }
                  });

                  const cellBorder = '1px solid #e5e7eb';
                  const formatNumber = (amount) => Number(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  const currencyShort = { TRY: 'TL', USD: 'Dolar', EUR: 'Euro', GOLD: 'Has Altın', SILVER: 'Has Gümüş' };
                  const thStyle = {
                    padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: '#1e293b',
                    fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px',
                    background: '#e2e8f0', borderBottom: '2px solid #94a3b8',
                    borderRight: cellBorder, whiteSpace: 'nowrap'
                  };
                  const tdStyle = {
                    padding: '8px 12px', borderBottom: cellBorder, borderRight: cellBorder,
                    fontSize: '12px', whiteSpace: 'nowrap'
                  };
                  const numCell = { fontFamily: '"SF Mono","Menlo","Consolas",monospace', fontSize: '13px', fontVariantNumeric: 'tabular-nums' };

                  const editHistoryEntry = async (historyId) => {
                    const h = wHistories.find(hh => hh.id === historyId);
                    if (h) {
                      const p = group.payments.find(pp => pp.id === h.payment_id);
                      if (p) {
                        await openPartialModal(p);
                        setEditingHistoryId(h.id);
                        setEditingHistoryAmount(String(Number(h.amount)));
                        setEditingHistoryMethod(h.payment_method || 'cash');
                      }
                    }
                  };
                  const deleteEntry = (entry) => {
                    const isDebit = entry.type === 'debit';
                    const isCreditBalance = entry.type === 'credit_balance';
                    const isPayment = entry.type === 'credit';
                    const recordLabel = isDebit ? 'Borç' : isCreditBalance ? 'Alacak' : 'Ödeme';
                    const parts = entry.parts || [entry];
                    showConfirmation({
                      title: `${recordLabel} Kaydını Sil`,
                      message: entry.parts
                        ? `"${entry.title}" ödemesi (${formatNumber(entry.amount)}, ${parts.length} borca dağıtılmış) tümüyle silinecek. Emin misiniz?`
                        : `"${entry.title}" ${recordLabel.toLowerCase()} kaydını silmek istediğinize emin misiniz?`,
                      confirmText: 'Sil',
                      type: 'danger',
                      onConfirm: async () => {
                        let done = 0;
                        try {
                          for (const part of parts) {
                            const url = isPayment
                              ? `${API_URL}/wholesaler-payment-history/${part.history_id}`
                              : `${API_URL}/wholesaler-payments/${part.payment_id}`;
                            const fetchOpts = { method: 'DELETE' };
                            if (isPayment) {
                              fetchOpts.headers = { 'Content-Type': 'application/json' };
                              fetchOpts.body = JSON.stringify({ payment_id: part.payment_id });
                            }
                            const res = await fetch(url, fetchOpts);
                            if (!res.ok) throw new Error(`HTTP ${res.status}`);
                            done++;
                          }
                          toast.success(`${recordLabel} kaydı silindi!`);
                        } catch (error) {
                          toast.error(done > 0 ? `Silme yarıda kaldı (${done}/${parts.length}). Lütfen geçmişi kontrol edin.` : 'Silme başarısız!');
                        } finally {
                          if (done > 0) fetchPayments();
                        }
                      }
                    });
                  };

                  return (
                    <table className="wp-wd-ledger" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '44px' }} />
                        <col style={{ width: '95px' }} />
                        <col style={{ width: '240px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '170px' }} />
                        <col style={{ width: '78px' }} />
                        <col style={{ width: '80px' }} />
                      </colgroup>
                      <thead>
                        <tr style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <th style={{ ...thStyle, paddingLeft: '16px', textAlign: 'center' }}>#</th>
                          <th style={thStyle}>Tarih</th>
                          <th style={thStyle}>Açıklama</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Birim</th>
                          <th style={{ ...thStyle, textAlign: 'right', color: '#b91c1c' }}>Borç</th>
                          <th style={{ ...thStyle, textAlign: 'right', color: '#15803d' }}>Ödeme</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Bakiye</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Fatura</th>
                          <th style={{ ...thStyle, textAlign: 'center', borderRight: 'none' }}>İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entriesWithBalance.map((entry, idx) => (
                          <Fragment key={entry.id}>
                          <tr className="wp-wd-row" style={{ background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f8fafc'; }}
                          >
                            <td className="wp-wd-c-no" style={{ ...tdStyle, paddingLeft: '16px', color: '#94a3b8', fontSize: '11px', textAlign: 'center', fontFamily: numCell.fontFamily }}>{idx + 1}</td>
                            <td className="wp-wd-c-date" style={{ ...tdStyle, color: '#334155' }}>
                              <div style={{ fontFamily: numCell.fontFamily, fontSize: '12px' }}>{entry.date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                              <div style={{ fontSize: '10px', color: '#94a3b8', fontFamily: numCell.fontFamily }}>{entry.date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                            </td>
                            <td className="wp-wd-c-desc" style={{ ...tdStyle, color: '#1e293b', fontWeight: 500, overflow: 'hidden' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                                <span style={{
                                  display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', flexShrink: 0,
                                  background: entry.type === 'debit' ? '#ef4444' : '#22c55e'
                                }} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }} title={entry.title}>{entry.title}</span>
                                {entry.parts && (
                                  <button type="button" onClick={() => setExpandedPayGroups(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                                    title="Borçlara dağılımı göster"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', flexShrink: 0, padding: '2px 6px', border: '1px solid #bbf7d0', borderRadius: '4px', background: '#f0fdf4', color: '#15803d', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>
                                    {entry.parts.length} borca dağıtıldı {expandedPayGroups[entry.id] ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                  </button>
                                )}
                                {entry.photos?.length > 0 && (
                                  <button type="button" onClick={() => setWsPhotoViewer({ photos: entry.photos, index: 0 })}
                                    title={`${entry.photos.length} fotoğraf — görüntüle`}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', flexShrink: 0, padding: '2px 6px', border: '1px solid #bfdbfe', borderRadius: '4px', background: '#eff6ff', color: '#1d4ed8', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>
                                    <Camera size={11} /> {entry.photos.length}
                                  </button>
                                )}
                              </div>
                              {entry.type === 'debit' && entry.status && (
                                <span style={{
                                  fontSize: '9px', padding: '1px 6px', borderRadius: '3px', marginTop: '3px', display: 'inline-block',
                                  background: entry.status === 'paid' ? '#dcfce7' : entry.status === 'partial' ? '#e0f2fe' : '#fef9c3',
                                  color: entry.status === 'paid' ? '#15803d' : entry.status === 'partial' ? '#0369a1' : '#a16207',
                                  fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px'
                                }}>
                                  {entry.status === 'paid' ? 'Ödendi' : entry.status === 'partial' ? 'Kısmi' : 'Bekliyor'}
                                </span>
                              )}
                              {entry.type === 'credit_balance' && (
                                <span style={{
                                  fontSize: '9px', padding: '1px 6px', borderRadius: '3px', marginTop: '3px', display: 'inline-block',
                                  background: '#dcfce7', color: '#15803d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px'
                                }}>
                                  Alacak
                                </span>
                              )}
                            </td>
                            <td className="wp-wd-c-unit" style={{ ...tdStyle, textAlign: 'center' }}>
                              <span style={{ fontSize: '11px', padding: '2px 8px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '3px', color: '#1e293b', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {currencyInfo[entry.currency]?.icon} {currencyShort[entry.currency] || entry.currency}
                              </span>
                            </td>
                            <td className={`wp-wd-c-debit${entry.type === 'debit' ? '' : ' is-empty'}`} style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, ...numCell, color: entry.type === 'debit' ? '#b91c1c' : '#cbd5e1', background: entry.type === 'debit' ? 'rgba(239,68,68,0.04)' : undefined, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {entry.type === 'debit' ? formatNumber(entry.amount) : '—'}
                            </td>
                            <td className={`wp-wd-c-credit${entry.type === 'debit' ? ' is-empty' : ''}`} style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, ...numCell, color: (entry.type === 'credit' || entry.type === 'credit_balance') ? '#15803d' : '#cbd5e1', background: (entry.type === 'credit' || entry.type === 'credit_balance') ? 'rgba(34,197,94,0.04)' : undefined, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {(entry.type === 'credit' || entry.type === 'credit_balance') ? formatNumber(entry.amount) : '—'}
                            </td>
                            <td className="wp-wd-c-balance" style={{ ...tdStyle, textAlign: 'right', fontWeight: 800, ...numCell, color: entry.balance > 0 ? '#b91c1c' : entry.balance < 0 ? '#15803d' : '#64748b', background: '#f8fafc' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', flexWrap: 'nowrap' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatNumber(Math.abs(entry.balance))}</span>
                                {entry.balance !== 0 && (
                                  <span style={{
                                    fontSize: '9px', fontWeight: 700, padding: '1px 5px', borderRadius: '3px',
                                    background: entry.balance > 0 ? '#fee2e2' : '#dcfce7',
                                    color: entry.balance > 0 ? '#991b1b' : '#166534',
                                    border: `1px solid ${entry.balance > 0 ? '#fca5a5' : '#86efac'}`,
                                    flexShrink: 0, letterSpacing: '0.3px'
                                  }}>{entry.balance > 0 ? 'BORÇ' : 'ALACAK'}</span>
                                )}
                              </div>
                            </td>
                            <td className="wp-wd-c-invoice" style={{ ...tdStyle, textAlign: 'center' }}>
                              <button
                                onClick={() => toggleWDetailInvoice(entry)}
                                style={{
                                  padding: '3px 8px', borderRadius: '4px', border: 'none', cursor: 'pointer',
                                  background: entry.invoice_issued ? '#dcfce7' : '#fef2f2',
                                  color: entry.invoice_issued ? '#16a34a' : '#dc2626',
                                  fontSize: '9px', fontWeight: 600, transition: 'all 0.15s',
                                  display: 'inline-flex', alignItems: 'center', gap: '2px'
                                }}
                                title={entry.invoice_issued ? 'Fatura kesildi - tıklayarak değiştir' : 'Fatura kesilmedi - tıklayarak işaretle'}
                              >
                                {entry.invoice_issued ? <><Check size={10} /> Evet</> : <><X size={10} /> Hayır</>}
                              </button>
                            </td>
                            <td className="wp-wd-c-actions" style={{ ...tdStyle, textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '3px', justifyContent: 'center' }}>
                                {(entry.type === 'debit' || entry.type === 'credit_balance') ? (
                                  <button onClick={() => {
                                    const p = group.payments.find(pp => pp.id === entry.payment_id);
                                    if (p) {
                                      setEditingPayment(p);
                                      setOriginalPayment({ ...p });
                                      setFormData({
                                        title: p.title || '', description: p.description || '', amount: String(p.amount || ''),
                                        currency: p.currency || 'USD', payment_type: p.payment_type || 'expense',
                                        category: p.category || 'wholesaler', recipient: p.recipient || '',
                                        due_date: toLocalYMD(p.due_date),
                                        reminder_days: p.reminder_days || 3, recurring: p.recurring || false,
                                        recurring_interval: p.recurring_interval || 'monthly', status: p.status || 'pending',
                                        notes: p.notes || '', invoice_issued: p.invoice_issued || false,
                                        is_wholesaler: true, wholesaler_id: p.wholesaler_id || ''
                                      });
                                      setShowModal(true);
                                    }
                                  }}
                                    style={{ padding: '3px 6px', borderRadius: '4px', border: 'none', cursor: 'pointer', background: '#f0f4ff', color: '#3b82f6', fontSize: '9px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', transition: 'all 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#f0f4ff'; }}
                                    title={entry.type === 'credit_balance' ? 'Alacağı düzenle' : 'Borcu düzenle'}
                                  ><Pencil size={10} /></button>
                                ) : (
                                  <button onClick={() => entry.parts
                                    ? setExpandedPayGroups(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))
                                    : editHistoryEntry(entry.history_id)}
                                    style={{ padding: '3px 6px', borderRadius: '4px', border: 'none', cursor: 'pointer', background: '#f0fdf4', color: '#16a34a', fontSize: '9px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', transition: 'all 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                                    title={entry.parts ? 'Parçaları göster — tek tek düzenlenebilir' : 'Ödemeyi düzenle'}
                                  ><Pencil size={10} /></button>
                                )}
                                <button onClick={() => deleteEntry(entry)}
                                  style={{ padding: '3px 6px', borderRadius: '4px', border: 'none', cursor: 'pointer', background: '#fef2f2', color: '#dc2626', fontSize: '9px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', transition: 'all 0.15s' }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; }}
                                  title="Sil"
                                ><Trash2 size={10} /></button>
                              </div>
                            </td>
                          </tr>
                          {entry.parts && expandedPayGroups[entry.id] && entry.parts.map(part => (
                            <tr key={part.id} className="wp-wd-row wp-wd-subrow" style={{ background: '#f0fdf4' }}>
                              <td className="wp-wd-c-no" style={{ ...tdStyle, paddingLeft: '16px' }}></td>
                              <td className="wp-wd-c-date" style={{ ...tdStyle, color: '#94a3b8', fontSize: '10px', fontFamily: numCell.fontFamily }}>
                                <div>{part.date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                              </td>
                              <td className="wp-wd-c-desc" style={{ ...tdStyle, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', paddingLeft: '28px' }} title={part.debtTitle}>
                                ↳ Borç: {part.debtTitle || `#${part.payment_id}`}
                              </td>
                              <td className="is-empty" style={tdStyle}></td>
                              <td className="is-empty" style={{ ...tdStyle, textAlign: 'right', color: '#cbd5e1', ...numCell }}>—</td>
                              <td className="wp-wd-c-credit" style={{ ...tdStyle, textAlign: 'right', color: '#15803d', ...numCell, fontSize: '12px' }}>{formatNumber(part.amount)}</td>
                              <td className="is-empty" style={{ ...tdStyle, background: '#f8fafc' }}></td>
                              <td className="is-empty" style={tdStyle}></td>
                              <td className="wp-wd-c-actions" style={{ ...tdStyle, textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '3px', justifyContent: 'center' }}>
                                  <button onClick={() => editHistoryEntry(part.history_id)}
                                    style={{ padding: '3px 6px', borderRadius: '4px', border: 'none', cursor: 'pointer', background: '#f0fdf4', color: '#16a34a', display: 'inline-flex', alignItems: 'center' }}
                                    title="Bu parçayı düzenle"
                                  ><Pencil size={10} /></button>
                                  <button onClick={() => deleteEntry(part)}
                                    style={{ padding: '3px 6px', borderRadius: '4px', border: 'none', cursor: 'pointer', background: '#fef2f2', color: '#dc2626', display: 'inline-flex', alignItems: 'center' }}
                                    title="Bu parçayı sil"
                                  ><Trash2 size={10} /></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>

              {/* Dip Toplamlar - statik, scroll dışında */}
              {(() => {
                const totals = {};
                entries.forEach(e => {
                  const curr = e.currency;
                  if (!totals[curr]) totals[curr] = { debit: 0, credit: 0, invoicedDebit: 0, invoicedCredit: 0 };
                  if (e.type === 'debit') {
                    totals[curr].debit += e.amount;
                    if (e.invoice_issued) totals[curr].invoicedDebit += e.amount;
                  } else {
                    totals[curr].credit += e.amount;
                    if (e.invoice_issued) totals[curr].invoicedCredit += e.amount;
                  }
                });
                if (entries.length === 0) return null;
                const cellBorder2 = '1px solid #cbd5e1';
                const tdF = { padding: '8px 12px', fontSize: '13px', fontFamily: '"SF Mono","Menlo","Consolas",monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 700, whiteSpace: 'nowrap', borderRight: cellBorder2 };
                const thF = { padding: '6px 12px', fontSize: '10px', fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', background: '#e2e8f0', borderRight: cellBorder2, borderBottom: '2px solid #94a3b8' };
                return (
                  <div className="wp-wd-totals" style={{ borderTop: '2px solid #94a3b8', background: '#f8fafc', flexShrink: 0 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thF, textAlign: 'left', paddingLeft: '16px' }}>Birim</th>
                          <th style={{ ...thF, textAlign: 'right', color: '#b91c1c' }}>Toplam Borç</th>
                          <th style={{ ...thF, textAlign: 'right', color: '#15803d' }}>Toplam Ödenen</th>
                          <th style={{ ...thF, textAlign: 'right', paddingRight: '16px', borderRight: 'none' }}>Kalan / Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(totals).map(([curr, t]) => (
                          <tr key={curr} style={{ borderTop: cellBorder2 }}>
                            <td style={{ ...tdF, paddingLeft: '16px' }}>
                              <span style={{ fontSize: '12px', padding: '3px 10px', background: '#e2e8f0', border: '1px solid #cbd5e1', borderRadius: '3px', color: '#1e293b', fontWeight: 700, whiteSpace: 'nowrap' }}>{currencyInfo[curr]?.icon} {({ TRY: 'TL', USD: 'Dolar', EUR: 'Euro', GOLD: 'Has Altın', SILVER: 'Has Gümüş' })[curr] || curr}</span>
                            </td>
                            <td style={{ ...tdF, textAlign: 'right', color: '#b91c1c' }}>{Number(t.debit).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ ...tdF, textAlign: 'right', color: '#15803d' }}>{Number(t.credit).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ ...tdF, textAlign: 'right', paddingRight: '16px', borderRight: 'none' }}>
                              <span style={{
                                padding: '4px 12px', borderRadius: '4px',
                                background: t.debit - t.credit > 0 ? '#fee2e2' : t.debit - t.credit < 0 ? '#dcfce7' : '#f1f5f9',
                                border: `1px solid ${t.debit - t.credit > 0 ? '#fca5a5' : t.debit - t.credit < 0 ? '#86efac' : '#cbd5e1'}`,
                                color: t.debit - t.credit > 0 ? '#991b1b' : t.debit - t.credit < 0 ? '#166534' : '#475569',
                                fontSize: '13px', fontWeight: 800
                              }}>
                                {Number(Math.abs(t.debit - t.credit)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                <span style={{ fontSize: '10px', marginLeft: '4px', opacity: 0.75 }}>
                                  {t.debit - t.credit > 0 ? 'Borç' : t.debit - t.credit < 0 ? 'Alacak' : 'Net'}
                                </span>
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              </>}
            </div>
          </div>
        );
      })()}

      {/* Yeni Toptancı Modal */}
      {showWholesalerModal && (() => {
        const isEdit = !!editingWholesalerData;
        const initials = wholesalerFormData.name ? wholesalerFormData.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';
        const lbl = { fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' };
        const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' };

        return (
          <div className="wp-modal-overlay">
            <div style={{
              background: '#fff', borderRadius: '16px', width: '95%', maxWidth: '520px',
              boxShadow: '0 25px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
            }}>
              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)',
                padding: '20px 24px', color: 'white', display: 'flex', alignItems: 'center', gap: '14px'
              }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '18px', fontWeight: 700, flexShrink: 0
                }}>{initials}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>{isEdit ? 'Toptancı Düzenle' : 'Yeni Toptancı'}</div>
                  <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>
                    {isEdit ? 'Bilgileri güncelleyin' : 'Yeni toptancı kaydı oluşturun'}
                  </div>
                </div>
                <button onClick={() => setShowWholesalerModal(false)} style={{
                  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white', borderRadius: '8px', width: '32px', height: '32px',
                  cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>×</button>
              </div>

              {/* Form */}
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={lbl}>Toptancı Adı *</label>
                    <input type="text" value={wholesalerFormData.name} autoFocus
                      onChange={e => setWholesalerFormData({...wholesalerFormData, name: e.target.value})}
                      placeholder="Ad Soyad" style={inp}
                      onFocus={e => { e.target.style.borderColor = '#0f766e'; }}
                      onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                    />
                  </div>
                  <div>
                    <label style={lbl}>Firma Adı</label>
                    <input type="text" value={wholesalerFormData.company_name}
                      onChange={e => setWholesalerFormData({...wholesalerFormData, company_name: e.target.value})}
                      placeholder="Firma / Şirket" style={inp}
                      onFocus={e => { e.target.style.borderColor = '#0f766e'; }}
                      onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={lbl}>Telefon</label>
                    <input type="tel" value={wholesalerFormData.phone}
                      onChange={e => setWholesalerFormData({...wholesalerFormData, phone: e.target.value})}
                      placeholder="0xxx xxx xx xx" style={inp}
                      onFocus={e => { e.target.style.borderColor = '#0f766e'; }}
                      onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                    />
                  </div>
                  <div>
                    <label style={lbl}>E-posta</label>
                    <input type="email" value={wholesalerFormData.email}
                      onChange={e => setWholesalerFormData({...wholesalerFormData, email: e.target.value})}
                      placeholder="ornek@email.com" style={inp}
                      onFocus={e => { e.target.style.borderColor = '#0f766e'; }}
                      onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Adres</label>
                  <input type="text" value={wholesalerFormData.address}
                    onChange={e => setWholesalerFormData({...wholesalerFormData, address: e.target.value})}
                    placeholder="Şehir, ilçe, sokak..." style={inp}
                    onFocus={e => { e.target.style.borderColor = '#0f766e'; }}
                    onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                  />
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <label style={lbl}>Notlar</label>
                  <textarea value={wholesalerFormData.notes} rows="2"
                    onChange={e => setWholesalerFormData({...wholesalerFormData, notes: e.target.value})}
                    placeholder="Ek notlar..." style={{ ...inp, resize: 'none' }}
                    onFocus={e => { e.target.style.borderColor = '#0f766e'; }}
                    onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                  />
                </div>
              </div>

              {/* Footer */}
              <div style={{
                padding: '14px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0',
                display: 'flex', alignItems: 'center', gap: '10px'
              }}>
                {isEdit && (
                  <button onClick={() => { setShowWholesalerModal(false); handleDeleteWholesaler(editingWholesalerData); }}
                    style={{
                      padding: '10px 16px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
                      borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; }}
                  >Toptancıyı Sil</button>
                )}
                <div style={{ flex: 1 }}></div>
                <button onClick={() => setShowWholesalerModal(false)}
                  style={{
                    padding: '10px 20px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0',
                    borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                >İptal</button>
                <button onClick={handleSaveWholesaler}
                  disabled={!wholesalerFormData.name}
                  style={{
                    padding: '10px 24px', border: 'none', borderRadius: '8px',
                    fontSize: '13px', fontWeight: 700, cursor: wholesalerFormData.name ? 'pointer' : 'not-allowed',
                    background: wholesalerFormData.name ? '#0f766e' : '#d1d5db', color: 'white',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => { if (wholesalerFormData.name) e.currentTarget.style.background = '#115e59'; }}
                  onMouseLeave={e => { if (wholesalerFormData.name) e.currentTarget.style.background = '#0f766e'; }}
                >{isEdit ? 'Güncelle' : 'Kaydet'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Genel Onay Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.2s ease'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '400px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideUp 0.3s ease'
          }}>
            <div style={{
              background: confirmModalConfig.type === 'danger'
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : confirmModalConfig.type === 'warning'
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={24} color="white" />
              <h3 style={{
                margin: 0,
                color: 'white',
                fontSize: '18px',
                fontWeight: '600'
              }}>{confirmModalConfig.title}</h3>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{
                margin: '0 0 24px 0',
                color: '#4b5563',
                fontSize: '15px',
                lineHeight: '1.6',
                whiteSpace: 'pre-line'
              }}>{confirmModalConfig.message}</p>
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #e5e7eb',
                    background: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    transition: 'all 0.2s'
                  }}
                >
                  {confirmModalConfig.cancelText}
                </button>
                <button
                  onClick={() => {
                    confirmModalConfig.onConfirm();
                    setShowConfirmModal(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: confirmModalConfig.type === 'danger'
                      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                      : confirmModalConfig.type === 'warning'
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  {confirmModalConfig.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesalerPayments;
